import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaConciergeBell,
  FaCalendarCheck,
  FaCalendarAlt,
  FaPlus,
  FaPlusCircle,
  FaEye,
  FaEdit,
  FaTrash,
  FaMapMarkerAlt,
  FaFilter,
  FaSquare,
  FaPlay,
  FaClock,
  FaCheckCircle,
  FaTimes,
  FaShareSquare,
  FaThLarge,
  FaCheck,
  FaSearch,
  FaColumns,
  FaCheckSquare,
  FaList,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaCamera,
  FaUser,
  FaWhatsapp,
  FaEnvelope,
  FaBell,
  FaArrowRight,
  FaLocationArrow,
  FaChevronDown,
  FaAngleDoubleLeft,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleRight,
  FaReceipt,
  FaMoneyBillWave,
  FaFileExport,
  FaPhoneAlt,
  FaHashtag,
  FaStickyNote,
} from 'react-icons/fa';
import { FaShareSquare as FaShareSquareIcon } from 'react-icons/fa';
import Modal from '../components/Modal/Modal';
import CustomSelect from '../components/CustomSelect/CustomSelect';
import PageHero from '../components/PageHero/PageHero';
import './Event.scss';
import { storage } from '../utils/storage';
import { showToast } from '../utils/toast';
import useConfirm from '../hooks/useConfirm';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import { STORAGE_KEYS } from '../utils/constants';
import { addNotification } from '../redux/slices/uiSlice';
import { saveColumnPreferences, updateColumnPreferences } from '../redux/slices/settingsSlice';
import { formatDateDisplay, formatDateISO, formatDateFile, formatDateTimeDisplay } from '../utils/date';
import { parseEventDate } from '../utils/eventDates';
import Highlight from '../utils/Highlight';
import ServiceTypeInput from '../components/ServiceTypeInput/ServiceTypeInput';
import LocationInput from '../components/LocationInput/LocationInput';
import { getServiceShortLocationText, isServiceMapLinkEnabled, getServiceMapUrl } from '../utils/location';
import ShareChoiceModal from '../components/ShareChoiceModal/ShareChoiceModal';
import EventCalendarFab from '../components/EventCalendarFab/EventCalendarFab';
import { buildAssignmentWhatsApp, buildAssignmentEmail, buildEventWhatsApp, buildEventEmail } from '../utils/shareTemplates';

// Temporary draft storage key (session-only)
const DRAFT_KEY = 'photoflow_event_draft';
const EVENT_COLUMN_DEFAULTS = {
  id: false,
  quotationNo: false,
  date: true,
  services: true,
  orderType: false,
  invitedBy: true,
  team: true,
  location: true,
  status: true,
};

// Simple client field helpers — phone must be exactly 10 digits, numbers only
const sanitizePhone = (v) => (v || '').toString().replace(/\D/g, '').slice(0, 10);
const isValidPhone = (v) => /^\d{10}$/.test(sanitizePhone(v));
const isValidName = (v) => typeof v === 'string' && v.trim().length > 0 && /^[A-Za-z '\-\.()]{1,100}$/.test(v.trim());
const isValidEmail = (v) => { if (!v) return true; return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); };
// Prefer explicit `id` (serial like EVNT-2026-000002) over Mongo `_id` when available
const getEventIdentifier = (event) => String(event?.id || event?._id || event?.quotationId || '').trim();
const normalizeEventRecord = (event) => {
  if (!event) return event;
  const identifier = getEventIdentifier(event);
  return identifier ? { ...event, id: identifier } : event;
};

const normalizeOrderType = (value) => {
  const orderType = String(value || '').trim().toLowerCase();
  if (orderType === 'sub') return 'suborder';
  if (orderType === 'suborder' || orderType === 'own') return orderType;
  return 'own';
};

// "Invited By" (formerly "Referred By"): B's sub-order is pre-filled with A's
// (assigner's) name when A assigns an event to B. Reads new `invitedBy` first,
// then legacy `referredBy`, then `sourceOwnerName` / `_assigner` for older docs.
const getInvitedBy = (ev) => String(
  ev?.invitedBy || ev?.referredBy || ev?.sourceOwnerName || ev?._assigner?.name || ev?._assigner?.email || ''
).trim();

const broadcastEventsChanged = () => {
  try {
    window.dispatchEvent(new CustomEvent('photoflow:events-changed'));
  } catch (e) {}
};

// Light-red highlight when an event has no usable date (no service date,
// no top-level date/startDate). Used for table rows and grid cards.
const isEventWithoutDate = (event) => {
  if (!event) return true;
  if (parseEventDate(event.date) || parseEventDate(event.startDate)) return false;
  const services = Array.isArray(event.services) ? event.services : [];
  if (services.some((s) => parseEventDate(s?.date))) return false;
  return true;
};

// Shared avatar identity for team member chips/cards (initials + hue).
const getMemberChipIdentity = (name) => {
  const clean = String(name || '').trim();
  const initials = clean.split(/\s+/).slice(0, 2).map((w) => w.charAt(0)).join('').toUpperCase() || '?';
  let hue = 0;
  for (let i = 0; i < clean.length; i += 1) hue = (hue + clean.charCodeAt(i) * (i + 1)) % 360;
  return { initials, hue };
};

// Resolve a team member's photo (linked Mivent user photo or uploaded avatar).
const resolveMemberAvatar = (fm) => [fm?.linkedUserPhotoURL, fm?.avatar, fm?.photoURL, fm?.photo]
  .map((v) => (typeof v === 'string' ? v.trim() : ''))
  .find((v) => v.length > 0) || '';

const Event = () => {
  const { t } = useLanguage();
  const { columnPreferences } = useSelector((state) => state.settings);
  const authUser = useSelector((state) => state.auth?.user) || null;
  // Assigner identity for polished manual-share messages (non-Mivent members)
  const assignerName = authUser?.name || session.getUser()?.name || session.getUser()?.email || "";
  const assignerEmail = authUser?.email || session.getUser()?.email || "";
  const assignerPhone = authUser?.phone || session.getUser()?.phone || "";
  // Initialize with empty array - data will be loaded from server only
  const [events, setEvents] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEventReminderEnabled, setIsEventReminderEnabled] = useState(false);
  // Ping (resend notification) loading state for the View modal team section
  const [pingLoading, setPingLoading] = useState(false);
  const [pingMember, setPingMember] = useState('');
  // Create-event modal reminder toggle
  const [createEventReminderEnabled, setCreateEventReminderEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('scheduled');
  const [statFilter, setStatFilter] = useState('scheduled');
  // New filters: order type and date range
  const [orderTypeFilter, setOrderTypeFilter] = useState('all'); // 'all' | 'own' | 'suborder'
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState(''); // YYYY-MM-DD
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'client'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  // Default to grid view on mobile devices (width < 768px)
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 'grid';
    }
    return 'table';
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(EVENT_COLUMN_DEFAULTS);
  // Refs for column menu and toggle button to detect outside clicks
  const eventMenuRef = useRef(null);
  const eventBtnRef = useRef(null);
  const orderTypeDropdownRef = useRef(null);
  const [orderTypeDropdownOpen, setOrderTypeDropdownOpen] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    quotationId: '',
    quotationRef: '', // Reference to the selected quotation (if any)
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    eventStatus: 'scheduled',
    // New order fields
    orderType: 'own', // 'own' or 'suborder'
    invitedBy: '', // only used when orderType === 'suborder' (legacy alias: referredBy)
    referredBy: '', // legacy mirror of invitedBy — kept for backward compat
    notifyAssignees: true, // send notification to assigned members (toggle, ON by default)
    // store team as an array of member names
    team: [],
        services: [
        {
          name: '',
          date: '',
          time: '',
          location: '',
          locationDetail: null,
          status: 'scheduled',
          notes: '',
          // UI-only flags: unified flag to indicate date/time/location same as previous
          sameAsDateAbove: false,
          sameAsTimeAbove: false,
          sameAsLocationAbove: false,
          sameAsAbove: false
        }
      ],
    notes: ''
  });
  const confirm = useConfirm();

  useEffect(() => {
    const savedColumns = columnPreferences?.events;
    if (savedColumns && typeof savedColumns === 'object' && !Array.isArray(savedColumns)) {
      const migrated = { ...savedColumns };
      // Migrate legacy `referredBy` column pref to `invitedBy`
      if (migrated.referredBy !== undefined && migrated.invitedBy === undefined) {
        migrated.invitedBy = migrated.referredBy;
      }
      setVisibleColumns({
        ...EVENT_COLUMN_DEFAULTS,
        ...migrated,
      });
    }
  }, [columnPreferences?.events]);

  // Close column menu when clicking outside (but ignore clicks on the toggle button)
  useEffect(() => {
    function handleOutsideClick(e) {
      if (orderTypeDropdownOpen && orderTypeDropdownRef.current && !orderTypeDropdownRef.current.contains(e.target)) {
        setOrderTypeDropdownOpen(false);
      }
      if (!showColumnMenu) return;
      const target = e.target;
      if (eventMenuRef.current && eventMenuRef.current.contains(target)) return;
      if (eventBtnRef.current && eventBtnRef.current.contains(target)) return;
      setShowColumnMenu(false);
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showColumnMenu, orderTypeDropdownOpen]);

  // Service options: prefer persisted SERVICES, fall back to empty list
  const [serviceOptions, setServiceOptions] = useState(() => {
    try {
      const persisted = storage.getJSON(STORAGE_KEYS.SERVICES, null);
      if (Array.isArray(persisted) && persisted.length > 0) {
        return persisted.map(s => s.name).filter(Boolean);
      }
    } catch (e) {}
    return [];
  });

  // Available services as objects (id, name, price) - prefer persisted SERVICES like Quotations/Billing
  const [availableServices, setAvailableServices] = useState(() => {
    try {
      const persisted = storage.getJSON(STORAGE_KEYS.SERVICES, null);
      if (Array.isArray(persisted) && persisted.length > 0) {
        return persisted.map(s => ({ id: s.id, name: s.name, price: Number(s.basePrice) || Number(s.price) || 0 }));
      }
    } catch (e) {}
    return [];
  });

  // Track pending new services that haven't been saved to database yet
  const [pendingNewServices, setPendingNewServices] = useState([]);

  // Combine available services and pending new services for dropdown display
  const allServices = useMemo(() => {
    return [...availableServices, ...pendingNewServices];
  }, [availableServices, pendingNewServices]);

  // Try to refresh services from server when available and keep serviceOptions in sync
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const remote = await storage.syncFromServer(STORAGE_KEYS.SERVICES);
        if (mounted && Array.isArray(remote) && remote.length > 0) {
          const mapped = remote.map(s => ({ id: s.id, name: s.name, price: Number(s.basePrice) || Number(s.price) || 0 }));
          setAvailableServices(mapped);
          // keep the simple name list in serviceOptions for compatibility with existing code paths
          setServiceOptions(mapped.map(s => s.name));
        }
      } catch (e) {
        // ignore - keep persisted or fallback options
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Track a new service locally (will be saved to database only when form is submitted)
  const handleNewService = async (serviceName) => {
    if (!serviceName || !serviceName.trim()) return;
    
    const trimmedName = serviceName.trim();
    
    // Check if service already exists in either available or pending lists (case-insensitive)
    const existsInAvailable = availableServices.some(s => 
      (s.name || '').toLowerCase() === trimmedName.toLowerCase()
    );
    const existsInPending = pendingNewServices.some(s => 
      (s.name || '').toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (existsInAvailable || existsInPending) return;

    const newService = {
      id: `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: trimmedName,
      category: 'Uncategorized',
      description: '',
      basePrice: 0,
      deliverables: [],
      features: [],
    };

    // Add to pending services (not saved to database yet)
    setPendingNewServices(prev => [...prev, { id: newService.id, name: newService.name, price: 0 }]);
    setServiceOptions(prev => [...prev, newService.name]);
  };

  // Save a single pending service to the database
  const savePendingServiceToDatabase = async (service) => {
    try {
      const token = session.getToken();
      if (!token) return null;

      const res = await fetch(`${API_BASE_URL}/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(service),
      });

      if (res.ok) {
        const created = await res.json();
        return { id: created._id || created.id, name: created.name, price: Number(created.basePrice) || 0 };
      }
    } catch (e) {
      console.error('Failed to save new service:', e);
    }
    return null;
  };

  // Listen for notification events while on /events so clicking notifications
  // will open the requested event view even if the user is already on Events.
  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/events') return;
        const state = detail.state || {};
        if (state.openCreate) {
          handleCreateEvent();
        }
        if (state.openViewId) {
          const idToOpen = String(state.openViewId);
          const e = events.find(x => {
            const identifiers = [x.id, x._id, x.quotationId].map(v => v ? String(v) : '');
            return identifiers.includes(idToOpen);
          });
          if (e) {
            handleViewEvent(e);
          } else {
            // Not owned by B — try fetching as shared/assigned event
            openSharedEvent(idToOpen);
            pendingOpenRef.current = idToOpen;
          }
        }
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [events]);

  // Team members (names) sourced from persisted TEAM storage
  const [teamMembers, setTeamMembers] = useState(() => {
    try {
      const persisted = storage.getJSON(STORAGE_KEYS.TEAM, []);
      if (Array.isArray(persisted) && persisted.length > 0) {
        return persisted.map(m => m.name).filter(Boolean);
      }
    } catch (e) {}
    return [];
  });

  // Try to fetch team members from the server when authenticated. The
  // storage.getJSON helper is a no-op in some builds (server-only storage),
  // so we explicitly call syncFromServer to ensure team list is loaded.
  // Also keep full objects for payments handling
  const [fullTeamMembers, setFullTeamMembers] = useState(() => {
    try {
      const persisted = storage.getJSON(STORAGE_KEYS.TEAM, []);
      if (Array.isArray(persisted) && persisted.length > 0) return persisted;
    } catch (e) {}
    return [];
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const remote = await storage.syncFromServer(STORAGE_KEYS.TEAM);
        if (mounted && Array.isArray(remote) && remote.length > 0) {
          setTeamMembers(remote.map(m => (m && (m.name || m.fullName || m.displayName)) || '').filter(Boolean));
          setFullTeamMembers(remote);
        }
      } catch (e) {
        // ignore network/auth errors - keep any persisted list
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Quick add team member modal state
  const [showTeamQuickAdd, setShowTeamQuickAdd] = useState(false);
  const [quickTeamForm, setQuickTeamForm] = useState({ name: '', role: '', phone: '', email: '' });
  const [quickTeamErrors, setQuickTeamErrors] = useState({});
  const [quickTeamSaving, setQuickTeamSaving] = useState(false);

  // Inline team payment modal state (no navigation)
  const [showTeamPaymentModal, setShowTeamPaymentModal] = useState(false);
  const [paymentMemberName, setPaymentMemberName] = useState('');
  const [paymentMemberObj, setPaymentMemberObj] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ eventId: '', eventName: '', amount: '', date: new Date().toISOString().split('T')[0], status: 'pending', notes: '' });
  const [paymentErrors, setPaymentErrors] = useState({});
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [useCustomEventName, setUseCustomEventName] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);

  // Manual share modal for non-Mivent assignees
  const [showManualShareModal, setShowManualShareModal] = useState(false);
  const [manualShareTargets, setManualShareTargets] = useState([]); // [{name,email,phone,reason}]
  const [manualShareEvent, setManualShareEvent] = useState(null);
  // Client share dialog: To Client (send directly) vs To Others (copy template)
  const [clientShareTarget, setClientShareTarget] = useState(null);
  const [clientShareChannel, setClientShareChannel] = useState('whatsapp'); // 'whatsapp' | 'email'
  const resolveMemberRole = (memberName) => {
    try {
      const fm = (fullTeamMembers || []).find((x) => String(x?.name || "").trim().toLowerCase() === String(memberName || "").trim().toLowerCase());
      return fm?.role ? String(fm.role).trim() : "";
    } catch { return ""; }
  };

  // Shared (assigned) event view — B views A's event and saves as sub-order
  const [sharedEvent, setSharedEvent] = useState(null);
  const [showSharedModal, setShowSharedModal] = useState(false);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [saveSubOrderLoading, setSaveSubOrderLoading] = useState(false);

  // Payments viewer (All Payments) state for inline view
  const [isPaymentsViewOpen, setIsPaymentsViewOpen] = useState(false);
  const [paymentsViewerMember, setPaymentsViewerMember] = useState(null);
  const [paymentsSearch, setPaymentsSearch] = useState('');
  const [paymentsPage, setPaymentsPage] = useState(1);
  const paymentsItemsPerPage = 10;

  const handleQuickTeamChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setQuickTeamForm((prev) => ({ ...prev, [name]: (value || '').replace(/\D/g, '').slice(0, 10) }));
      return;
    }
    setQuickTeamForm((prev) => ({ ...prev, [name]: value }));
  };
  const validateQuickTeam = () => {
    const err = {};
    if (!quickTeamForm.name || !quickTeamForm.name.trim()) err.name = 'Name is required';
    if (quickTeamForm.phone && !/^\d{10}$/.test((quickTeamForm.phone || '').replace(/\D/g, ''))) err.phone = 'Enter a valid 10-digit phone number (numbers only)';
    if (quickTeamForm.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(quickTeamForm.email)) err.email = 'Enter a valid email address';
    return err;
  };
  const saveQuickTeamMember = async () => {
    const validation = validateQuickTeam();
    if (Object.keys(validation).length) { setQuickTeamErrors(validation); return; }
    setQuickTeamSaving(true);
    const payload = {
      name: String(quickTeamForm.name || '').trim(),
      role: String(quickTeamForm.role || '').trim(),
      phone: String(quickTeamForm.phone || '').replace(/\D/g, '').slice(0, 10),
      email: String(quickTeamForm.email || '').trim().toLowerCase(),
    };
    try {
      const token = session.getToken();
      if (!token) { showToast('Please login to add team members', 'error'); setQuickTeamSaving(false); return; }
      const res = await fetch(`${API_BASE_URL}/team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Failed to create team member' }));
        showToast(errData.error || 'Failed to create team member', 'error'); setQuickTeamSaving(false); return;
      }
      const created = await res.json();
      const newName = created?.name || payload.name;
      setTeamMembers((prev) => prev.includes(newName) ? prev : [newName, ...prev]);
      setFullTeamMembers((prev) => [...prev, created]);
      setFormData((prev) => {
        const teamArr = Array.isArray(prev.team) ? [...prev.team] : [];
        if (!teamArr.includes(newName)) teamArr.push(newName);
        return { ...prev, team: teamArr };
      });
      try { window.dispatchEvent(new CustomEvent('photoflow:team-changed')); } catch (e) {}
      if (created && created.isMiventUser === false) {
        showToast(`${newName} added — not on Mivent yet. Invite via Team page or share event via WhatsApp/Email.`, { type: 'warning', duration: 5000 });
      } else {
        showToast('Team member added', { type: 'success' });
      }
      setShowTeamQuickAdd(false);
      setQuickTeamForm({ name: '', role: '', phone: '', email: '' });
      setQuickTeamErrors({});
    } catch (e) { showToast('Failed to add team member', 'error'); }
    finally { setQuickTeamSaving(false); }
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };
  const handlePaymentEventChange = (eventId) => {
    if (useCustomEventName) { setPaymentForm((prev) => ({ ...prev, eventId })); return; }
    if (!eventId) { setPaymentForm((prev) => ({ ...prev, eventId: '', eventName: '' })); return; }
    const selectedEvent = events.find(ev => String(ev.id) === String(eventId));
    if (selectedEvent) {
      setPaymentForm((prev) => ({ ...prev, eventId: String(selectedEvent.id), eventName: selectedEvent.clientName || `Event ${selectedEvent.quotationId || selectedEvent.id}` }));
    } else { setPaymentForm((prev) => ({ ...prev, eventId })); }
  };
  const handlePaymentStatusChange = (status) => setPaymentForm((prev) => ({ ...prev, status }));
  const validatePaymentForm = () => {
    const err = {};
    if (!useCustomEventName && !paymentForm.eventId) err.eventId = 'Please select an event';
    if (useCustomEventName && !paymentForm.eventName?.trim()) err.eventName = 'Please enter an event name';
    if (!paymentForm.amount || isNaN(paymentForm.amount) || Number(paymentForm.amount) <= 0) err.amount = 'Enter a valid amount';
    if (!paymentForm.date) err.date = 'Payment date is required';
    return err;
  };
  const saveTeamPayment = async () => {
    const validation = validatePaymentForm();
    if (Object.keys(validation).length) { setPaymentErrors(validation); return; }
    setPaymentSaving(true);
    const payment = {
      id: editingPaymentId || `pay-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
      eventId: paymentForm.eventId,
      eventName: paymentForm.eventName,
      amount: parseFloat(paymentForm.amount),
      date: paymentForm.date,
      status: paymentForm.status,
      notes: paymentForm.notes
    };
    try {
      const token = session.getToken();
      if (!token) { showToast('Please login to add payment', 'error'); setPaymentSaving(false); return; }
      // Always fetch fresh team list to reliably resolve member by name (handles stale fullTeamMembers)
      let member = null;
      try {
        const teamRes = await fetch(`${API_BASE_URL}/team`, { headers: { Authorization: `Bearer ${token}` } });
        if (teamRes.ok) {
          const teamData = await teamRes.json();
          const list = Array.isArray(teamData) ? teamData : [];
          // keep local cache in sync
          if (list.length > 0) setFullTeamMembers(list);
          const target = String(paymentMemberName || '').trim().toLowerCase();
          member = list.find(m => String(m?.name||'').trim().toLowerCase() === target);
          // fallback: if not found, try matching by _id from paymentMemberObj or paymentsViewerMember
          if (!member && paymentMemberObj?._id) {
            member = list.find(m => String(m?._id) === String(paymentMemberObj._id));
          }
          if (!member && paymentsViewerMember?._id) {
            const viewerTarget = String(paymentsViewerMember?.name||'').trim().toLowerCase();
            if (viewerTarget === target) member = list.find(m => String(m?._id) === String(paymentsViewerMember._id));
          }
        }
      } catch (e) { /* ignore fetch error */ }
      // fallback to cached objects if fetch failed
      if (!member) {
        const target = String(paymentMemberName || '').trim().toLowerCase();
        member = fullTeamMembers.find(m => String(m?.name||'').trim().toLowerCase() === target) || paymentMemberObj || paymentsViewerMember;
      }
      if (!member || !member._id) {
        const available = fullTeamMembers.map(m => m.name).join(', ');
        console.warn('saveTeamPayment: member not found', { paymentMemberName, paymentMemberObj, fullTeamMembers, available });
        showToast(`Team member "${paymentMemberName}" not found${available ? ` (available: ${available})` : ''}`, 'error');
        setPaymentSaving(false);
        return;
      }
      const existingPayments = Array.isArray(member.payments) ? member.payments : [];
      const updatedPayments = editingPaymentId ? existingPayments.map(p => p.id === editingPaymentId ? { ...p, ...payment } : p) : [...existingPayments, payment];
      const putRes = await fetch(`${API_BASE_URL}/team/${encodeURIComponent(member._id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...member, payments: updatedPayments }),
      });
      if (putRes.ok) {
        const updated = await putRes.json();
        setFullTeamMembers((prev) => {
          const exists = prev.some(m => String(m._id) === String(updated._id));
          return exists ? prev.map(m => String(m._id) === String(updated._id) ? updated : m) : [...prev, updated];
        });
        setPaymentsViewerMember((prev) => prev && String(prev._id) === String(member._id) ? updated : prev);
        try { window.dispatchEvent(new CustomEvent('photoflow:team-changed')); } catch (e) {}
        showToast(editingPaymentId ? 'Payment updated successfully' : 'Payment added successfully', { type: 'success' });
        setShowTeamPaymentModal(false);
        setPaymentMemberName(''); setPaymentMemberObj(null); setEditingPaymentId(null); setUseCustomEventName(false);
      } else {
        const errData = await putRes.json().catch(() => ({ error: 'Failed to save payment' }));
        showToast(errData.error || 'Failed to save payment', 'error');
      }
    } catch (e) { console.error('saveTeamPayment error', e); showToast('Failed to save payment', 'error'); }
    finally { setPaymentSaving(false); }
  };
  const openEditPaymentForEvent = (member, payment) => {
    const targetMember = member || paymentsViewerMember;
    if (!targetMember) return;
    setPaymentMemberName(targetMember.name || paymentMemberName);
    setPaymentMemberObj(targetMember);
    setEditingPaymentId(payment.id);
    const hasEventId = !!payment.eventId && events.some(ev => String(ev.id) === String(payment.eventId));
    setUseCustomEventName(!hasEventId);
    let dateValue = new Date().toISOString().split('T')[0];
    const rawDate = payment.date ? String(payment.date) : '';
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(rawDate)) dateValue = rawDate;
    else {
      const dt = new Date(rawDate);
      if (isFinite(dt.getTime())) {
        const y = dt.getFullYear(); const m = String(dt.getMonth()+1).padStart(2,'0'); const d = String(dt.getDate()).padStart(2,'0');
        dateValue = `${y}-${m}-${d}`;
      }
    }
    setPaymentForm({ eventId: payment.eventId || '', eventName: payment.eventName || '', amount: payment.amount ?? '', date: dateValue, status: payment.status || 'pending', notes: payment.notes || '' });
    setPaymentErrors({});
    setShowTeamPaymentModal(true);
  };
  const deletePaymentForEvent = async (memberId, paymentId) => {
    const ok = await confirm('Delete this payment record?');
    if (!ok) return;
    const memberToUpdate = fullTeamMembers.find(m => String(m._id) === String(memberId)) || paymentsViewerMember;
    if (!memberToUpdate) return;
    const previous = [...fullTeamMembers];
    const updatedPayments = (memberToUpdate.payments || []).filter(p => p.id !== paymentId);
    const optimistic = { ...memberToUpdate, payments: updatedPayments };
    setFullTeamMembers(prev => prev.map(m => String(m._id) === String(memberId) ? optimistic : m));
    setPaymentsViewerMember(prev => prev && String(prev._id) === String(memberId) ? optimistic : prev);
    try {
      const token = session.getToken();
      if (!token) { setFullTeamMembers(previous); return; }
      const res = await fetch(`${API_BASE_URL}/team/${encodeURIComponent(memberId)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(optimistic) });
      if (!res.ok) { setFullTeamMembers(previous); setPaymentsViewerMember(paymentsViewerMember); showToast('Failed to delete payment', 'error'); }
      else { const updated = await res.json(); setFullTeamMembers(prev => prev.map(m => String(m._id) === String(memberId) ? updated : m)); setPaymentsViewerMember(prev => prev && String(prev._id) === String(memberId) ? updated : prev); try { window.dispatchEvent(new CustomEvent('photoflow:team-changed')); } catch (e) {} }
    } catch (e) { setFullTeamMembers(previous); console.error('Error deleting payment:', e); }
  };
  const updatePaymentStatusForEvent = async (memberId, paymentId, newStatus) => {
    const memberToUpdate = fullTeamMembers.find(m => String(m._id) === String(memberId)) || paymentsViewerMember;
    if (!memberToUpdate) return;
    const previous = [...fullTeamMembers];
    const updatedPayments = (memberToUpdate.payments || []).map(p => p.id === paymentId ? { ...p, status: newStatus } : p);
    const optimistic = { ...memberToUpdate, payments: updatedPayments };
    setFullTeamMembers(prev => prev.map(m => String(m._id) === String(memberId) ? optimistic : m));
    setPaymentsViewerMember(prev => prev && String(prev._id) === String(memberId) ? optimistic : prev);
    try {
      const token = session.getToken();
      if (!token) { setFullTeamMembers(previous); return; }
      const res = await fetch(`${API_BASE_URL}/team/${encodeURIComponent(memberId)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(optimistic) });
      if (!res.ok) { setFullTeamMembers(previous); showToast('Failed to update payment status', 'error'); }
      else { const updated = await res.json(); setFullTeamMembers(prev => prev.map(m => String(m._id) === String(memberId) ? updated : m)); setPaymentsViewerMember(prev => prev && String(prev._id) === String(memberId) ? updated : prev); try { window.dispatchEvent(new CustomEvent('photoflow:team-changed')); } catch (e) {} }
    } catch (e) { setFullTeamMembers(previous); console.error('Error updating payment status:', e); }
  };
  // Payment viewer helpers
  const formatPayDate = (dateInput) => {
    if (!dateInput && dateInput !== 0) return '—';
    try {
      const s = String(dateInput);
      const isoMatch = s.match(/^\d{4}-\d{1,2}-\d{1,2}$/);
      if (isoMatch) {
        const [y,m,d] = s.split('-');
        const dt = new Date(Number(y), Number(m)-1, Number(d));
        return dt.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
      }
      const dt = new Date(dateInput);
      if (!isFinite(dt.getTime())) return s;
      return dt.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
    } catch (e) { return String(dateInput); }
  };
  const getTotalPayments = (member) => !member || !Array.isArray(member.payments) ? 0 : member.payments.reduce((s,p)=>s+(p.amount||0),0);
  const getPaidAmount = (member) => !member || !Array.isArray(member.payments) ? 0 : member.payments.filter(p=>p.status==='paid').reduce((s,p)=>s+(p.amount||0),0);
  const getPendingAmount = (member) => !member || !Array.isArray(member.payments) ? 0 : member.payments.filter(p=>p.status==='pending'||p.status==='partial').reduce((s,p)=>s+(p.amount||0),0);
  const escapeCsvValue = (value) => {
    const text = String(value ?? '');
    if (!/[",\r\n,]/.test(text)) return text;
    return `"${text.replace(/"/g, '""')}"`;
  };
  const openPaymentsViewer = async (memberNameOrObj) => {
    let member = typeof memberNameOrObj === 'string' ? fullTeamMembers.find(m => String(m?.name||'').trim().toLowerCase() === String(memberNameOrObj).trim().toLowerCase()) : memberNameOrObj;
    // fallback fetch
    if (!member) {
      try {
        const token = session.getToken();
        if (token) {
          const res = await fetch(`${API_BASE_URL}/team`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data)) {
              setFullTeamMembers(data);
              const name = typeof memberNameOrObj === 'string' ? memberNameOrObj : memberNameOrObj?.name;
              member = data.find(m => String(m?.name||'').trim().toLowerCase() === String(name).trim().toLowerCase());
            }
          }
        }
      } catch (e) {}
    }
    if (!member) { showToast('Member not found', 'error'); return; }
    setPaymentsViewerMember(member);
    setIsPaymentsViewOpen(true);
    setPaymentsPage(1); setPaymentsSearch('');
  };
  const closePaymentsViewer = () => { setIsPaymentsViewOpen(false); setPaymentsViewerMember(null); setPaymentsPage(1); setPaymentsSearch(''); };
  const exportPaymentsForViewer = async () => {
    const confirmed = await confirm(`Export payments for ${String(paymentsViewerMember?.name || 'member')}?`, { title: 'Export Payments', confirmText: 'Export', cancelText: 'Cancel', showWarning: false });
    if (!confirmed || !paymentsViewerMember) return;
    const payments = Array.isArray(paymentsViewerMember.payments) ? [...paymentsViewerMember.payments].reverse() : [];
    const rows = payments.map((payment) => {
      const matchedEvent = events.find((ev) => String(ev.id) === String(payment.eventId));
      const displayEventId = payment.eventId || (matchedEvent ? matchedEvent.id : '');
      const displayEventName = payment.eventName || (matchedEvent ? (matchedEvent.clientName || `Event ${matchedEvent.quotationId || matchedEvent.id}`) : '');
      return [escapeCsvValue(displayEventId), escapeCsvValue(displayEventName), escapeCsvValue(payment.amount != null ? Number(payment.amount).toLocaleString('en-IN') : ''), escapeCsvValue(formatPayDate(payment.date)), escapeCsvValue(payment.status||''), escapeCsvValue(payment.notes||'')].join(',');
    });
    const csvContent = [['Event ID','Event','Payment','Payment Date','Status','Notes'].join(','), ...rows].join('\n');
    const safeName = String(paymentsViewerMember.name||'member').trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'')||'member';
    const fileName = `payments-${safeName}-${new Date().toISOString().slice(0,10)}.csv`;
    try {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = downloadUrl; link.download = fileName; document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(downloadUrl);
      showToast('Payments exported successfully', 'success');
    } catch (error) { showToast('Failed to export payments', 'error'); }
  };

  // Draft helpers: store temporary form draft in component state only
  const makeDraftKey = (id) => (id ? `${DRAFT_KEY}_${String(id)}` : DRAFT_KEY);

  const saveDraftToSession = (key) => {
    const data = { formData };
    sessionStorage.setItem(key, JSON.stringify(data));
  };

  const loadDraftFromSession = (key) => {
    try {
      const data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  };

  const removeDraftFromSession = (key) => {
    sessionStorage.removeItem(key);
  };

  // Auto-save draft when form data changes and modal is open
  useEffect(() => {
    if (showCreateModal && formData) {
      // Check if form has any data (not empty)
      const hasData = formData.clientName || formData.clientEmail || formData.clientPhone ||
                      formData.quotationId || formData.services.some(service => service.name || service.date || service.location) ||
                      formData.notes;
      if (hasData) {
        saveDraftToSession(DRAFT_KEY);
      }
    }
  }, [formData, showCreateModal]);

  // Available quotations loaded from server (database)
  const [quotations, setQuotations] = useState([]);

  // Local UI state for searchable quotation dropdown (event form)
  const [quotationQuery, setQuotationQuery] = useState('');
  const [showQuotationDropdown, setShowQuotationDropdown] = useState(false);
  const [highlightedQuotationIndex, setHighlightedQuotationIndex] = useState(-1);
  const quotationBlurTimeoutRef = useRef(null);
  const quotationInputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Try to fetch from server
        try {
          const remote = await storage.syncFromServer(STORAGE_KEYS.QUOTATIONS);
          if (Array.isArray(remote) && mounted) setQuotations(remote);
        } catch (e) {
          // ignore
        }
      } catch (e) {
        // ignore top-level errors
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Try to load events from server when authenticated. Falls back to local storage.
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const remote = await storage.syncFromServer(STORAGE_KEYS.EVENTS);
        if (!mounted) return;

        if (Array.isArray(remote)) {
          const seen = new Set();
          const deduped = [];

          for (const ev of remote) {
            const normalizedEvent = normalizeEventRecord(ev);
            const eventId = getEventIdentifier(normalizedEvent);
            if (!seen.has(eventId)) {
              seen.add(eventId);
              deduped.push(normalizedEvent);
            }
          }

          setEvents(deduped);
        }
      } catch (e) {
        // ignore
      }
    })();

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;

    const refreshTeamMembers = async () => {
      try {
        const remote = await storage.syncFromServer(STORAGE_KEYS.TEAM);
        if (mounted && Array.isArray(remote) && remote.length > 0) {
          setTeamMembers(remote.map(m => (m && (m.name || m.fullName || m.displayName)) || '').filter(Boolean));
          setFullTeamMembers(remote);
        }
      } catch (e) {
        // ignore network/auth errors - keep any persisted list
      }
    };

    const handleTeamChanged = () => {
      refreshTeamMembers();
    };

    window.addEventListener('photoflow:team-changed', handleTeamChanged);

    return () => {
      mounted = false;
      window.removeEventListener('photoflow:team-changed', handleTeamChanged);
    };
  }, []);

  const toInputDate = (value) => {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    // Already YYYY-MM-DD (or ISO datetime) — take the date part directly
    // to avoid timezone shifts from `new Date()`.
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) return formatDateISO(parsed);
    return '';
  };

  const handleSelectQuotation = (quotationId) => {
    if (!quotationId) {
      setFormData((fd) => ({ ...fd, quotationRef: '' }));
      return;
    }

    const q = quotations.find(x => [x.id, x.quotationId, x.ref].some(v => v != null && String(v) === String(quotationId)));
    // Prefill event schedule date from the quotation's main date
    const scheduleDate = toInputDate(q?.mainEventDate);
    setFormData((fd) => {
      // Build services from quotation services (array of names -> service objects)
      const mappedServices = Array.isArray(q?.services) && q.services.length > 0
        ? q.services.map(name => ({
            name,
            date: scheduleDate,
            time: '',
            location: '',
            locationDetail: null,
            status: 'scheduled',
            notes: '',
            sameAsDateAbove: false,
            sameAsTimeAbove: false,
            sameAsLocationAbove: false,
            sameAsAbove: false,
            typeEditable: true
          }))
        : [];

      const hasMeaningfulServices = Array.isArray(fd.services) && fd.services.some(s => s && s.name);

      // If the form already has services, fill empty dates with the quotation date
      // without overwriting dates the user already entered.
      const servicesWithDate = Array.isArray(fd.services) && scheduleDate
        ? fd.services.map(s => (s && !s.date ? { ...s, date: scheduleDate } : s))
        : fd.services;

      const nextOrderType = q ? normalizeOrderType(q.orderType || q.order_type) : fd.orderType;
      const prefillInvitedBy = nextOrderType === 'suborder'
        ? (getInvitedBy(fd) || getInvitedBy(q) || '')
        : '';
      return {
        ...fd,
  quotationRef: String(quotationId), // Store the selected quotation reference
        // Prefill client details if empty
        clientName: fd.clientName || (q && q.clientName) || fd.clientName,
        clientEmail: fd.clientEmail || (q && q.clientEmail) || fd.clientEmail,
        clientPhone: fd.clientPhone || (q && q.clientPhone) || fd.clientPhone,
        orderType: nextOrderType,
        // Clear invitedBy when switching back to own order; prefill from quotation when suborder
        invitedBy: prefillInvitedBy,
        referredBy: prefillInvitedBy,
        // If the form has no meaningful services, replace with mapped services from the quotation
        services: !hasMeaningfulServices && mappedServices.length > 0 ? mappedServices : servicesWithDate
      };
    });
  };

  const dispatch = useDispatch();

  // Get company details and notifications from Redux settings
  const { company, notifications } = useSelector((state) => state.settings);

  const getDefaultFormData = () => {
    const currentYear = new Date().getFullYear();
    return {
      quotationId: `EVNT-${currentYear}-${String(Date.now()).slice(-6)}`,
      quotationRef: '',
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      eventStatus: 'scheduled',
      orderType: 'own',
      invitedBy: '',
      referredBy: '',
      notifyAssignees: true,
      team: [],
      services: [
        {
          name: '',
          date: '',
          time: '',
          location: '',
          locationDetail: null,
          status: 'scheduled',
          notes: '',
          sameAsDateAbove: false,
          sameAsTimeAbove: false,
          sameAsLocationAbove: false,
          typeEditable: false
        }
      ],
      notes: ''
    };
  };

  const handleCreateEvent = () => {
    const defaultData = getDefaultFormData();

    // If there's a saved draft, load that draft into the form instead of the blank form
    const draft = loadDraftFromSession(makeDraftKey());
    if (draft && typeof draft === 'object' && draft.formData) {
      setFormData({ ...defaultData, ...draft.formData });
    } else {
      setFormData(defaultData);
    }
    setCreateEventReminderEnabled(notifications?.eventRemindersEnabled !== false);
    setShowCreateModal(true);
  };

  // Reset the create form to its initial values (does not close the modal)
  const resetCreateForm = () => {
    setFormData(getDefaultFormData());
    try { removeDraftFromSession(makeDraftKey()); } catch (e) {}
  };

  const handleViewEvent = (event) => {
    // Ensure orderType is present (fallback to 'own') so view/edit show correct value
    const withOrderType = normalizeEventRecord({ ...event, orderType: event.orderType || 'own' });
    const invitedByValue = getInvitedBy(withOrderType);
    setSelectedEvent(withOrderType);
    setFormData({
      quotationId: withOrderType.id,
      quotationRef: withOrderType.quotationRef || '', // Load quotation reference
      clientName: withOrderType.clientName,
      clientEmail: withOrderType.clientEmail,
      clientPhone: withOrderType.clientPhone,
      eventStatus: withOrderType.status,
      orderType: withOrderType.orderType,
      invitedBy: invitedByValue,
      referredBy: invitedByValue,
      notifyAssignees: withOrderType.notifyAssignees !== false,
      team: Array.isArray(withOrderType.team) ? [...withOrderType.team] : [],
        services: (withOrderType.services || []).map(s => ({
        ...s,
        sameAsDateAbove: false,
        sameAsTimeAbove: false,
        sameAsLocationAbove: false,
        sameAsAbove: false,
        typeEditable: true
      })),
      notes: withOrderType.notes
    });
    setIsEditMode(false);
    setShowViewModal(true);
  };

  // Shared event: B views A's assigned event (read-only) and can Save as Sub Order
  const fetchSharedEvent = async (id) => {
    const token = session.getToken();
    if (!token || !id) return null;
    setSharedLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/events/shared/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data;
    } catch (e) {
      return null;
    } finally {
      setSharedLoading(false);
    }
  };

  const openSharedEvent = async (id) => {
    // 1) Sender's own event: never show shared modal — sender doesn't need sub-order
    const owned = events.find(x => {
      const ids = [x.id, x._id, x.quotationId].map(v => v ? String(v) : '');
      return ids.includes(String(id));
    });
    if (owned) {
      handleViewEvent(owned);
      return;
    }
    // 2) Receiver already saved as sub-order: open his own copy instead of shared modal
    const sourceIdStr = String(id);
    const alreadySaved = events.find(x => String(x.sourceEventId||"") === sourceIdStr || String(x.sourceEventId||"") === String(id));
    if (alreadySaved) {
      handleViewEvent(alreadySaved);
      return;
    }
    const data = await fetchSharedEvent(id);
    if (!data || data.message === "Not found") {
      showToast("Assigned event details not available — may have been removed", { type:"warning", duration:4000 });
      return;
    }
    // If backend indicates it's owner viewing own event, treat as owned
    if (data._isOwner) {
      handleViewEvent(data);
      return;
    }
    // If B already has a sub-order for this source (checked via local list above), backend will also return _alreadyExists on save;
    // Double-check by looking again after fetch (source may be ObjectId)
    const srcKey = String(data._id || data.id || id);
    const exists2 = events.find(x => String(x.sourceEventId||"") === srcKey);
    if (exists2) {
      handleViewEvent(exists2);
      return;
    }
    setSharedEvent(data);
    setShowSharedModal(true);
  };

  const handleSaveAsSubOrder = async () => {
    if (!sharedEvent) return;
    const id = getEventIdentifier(sharedEvent);
    if (!id) return;
    setSaveSubOrderLoading(true);
    try {
      const token = session.getToken();
      const resp = await fetch(`${API_BASE_URL}/events/shared/${encodeURIComponent(id)}/save-as-suborder`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json().catch(()=> ({}));
      if (!resp.ok) {
        showToast(data.message || "Failed to save as sub-order", { type:"error" });
        return;
      }
      if (data._alreadyExists) {
        showToast("Already saved as sub-order", { type:"info" });
        setShowSharedModal(false);
        // navigate to the existing sub-order
        const eid = getEventIdentifier(data);
        setTimeout(()=> { window.dispatchEvent(new CustomEvent('ui:notification',{detail:{path:'/events', state:{openViewId:eid}}})); }, 200);
        return;
      }
      // Add to local list
      const normalized = normalizeEventRecord({ ...data, reminderEnabled: true, alarmEnabled: true });
      setEvents(prev => [normalized, ...prev.filter(e=> getEventIdentifier(e)!==getEventIdentifier(normalized))]);
      try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, [normalized, ...events.filter(e=> getEventIdentifier(e)!==getEventIdentifier(normalized))]); } catch(e){}
      broadcastEventsChanged();
      showToast(`Saved as sub-order ${normalized.id}`, { type:"success", duration:4000 });
      setShowSharedModal(false);
      setSharedEvent(null);
      // Auto-open the new sub-order in view mode and ensure reminder is on
      setTimeout(()=> handleViewEvent(normalized), 250);
    } catch (e) {
      showToast("Failed to save as sub-order", { type:"error" });
    } finally {
      setSaveSubOrderLoading(false);
    }
  };

  useEffect(() => {
    if (!showViewModal) {
      setIsEventReminderEnabled(false);
      return;
    }

    setIsEventReminderEnabled(Boolean(selectedEvent?.reminderEnabled || selectedEvent?.alarmEnabled));
  }, [showViewModal, selectedEvent?.id, selectedEvent?._id, selectedEvent?.quotationId]);

  // Immediately persist the reminder toggle to the backend on single click
  const handleToggleEventReminder = async () => {
    const newValue = !isEventReminderEnabled;
    setIsEventReminderEnabled(newValue); // optimistic update
    const eventId = getEventIdentifier(selectedEvent);
    try {
      const token = session.getToken();
      if (token && eventId) {
        const resp = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/reminder`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ reminderEnabled: newValue }),
        });
        if (resp.ok) {
          const updated = await resp.json();
          setSelectedEvent((prev) => ({ ...prev, reminderEnabled: newValue }));
          setEvents((prev) =>
            prev.map((ev) =>
              getEventIdentifier(ev) === eventId ? { ...ev, reminderEnabled: newValue } : ev,
            ),
          );
        } else {
          // Revert on failure
          setIsEventReminderEnabled(!newValue);
          showToast('Failed to update reminder. Please try again.');
        }
      }
    } catch (e) {
      setIsEventReminderEnabled(!newValue);
      showToast('Failed to update reminder. Please try again.');
    }
  };

  const handleEnableEdit = () => {
    // If there's a draft for this event, load it into the form; otherwise keep current formData
    try {
      const draft = loadDraftFromSession(makeDraftKey(selectedEvent && selectedEvent.id));
      if (draft && typeof draft === 'object') setFormData(draft);
    } catch (e) {}
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    // Reset form data to original event data
    if (selectedEvent) {
      const invitedByValue = getInvitedBy(selectedEvent);
      setFormData({
        quotationId: selectedEvent.id,
        quotationRef: selectedEvent.quotationRef || '',
        clientName: selectedEvent.clientName,
        clientEmail: selectedEvent.clientEmail,
        clientPhone: selectedEvent.clientPhone,
        eventStatus: selectedEvent.status,
        orderType: selectedEvent.orderType || 'own',
        invitedBy: invitedByValue,
        referredBy: invitedByValue,
        notifyAssignees: selectedEvent.notifyAssignees !== false,
        team: Array.isArray(selectedEvent.team) ? [...selectedEvent.team] : [],
        services: selectedEvent.services.map(s => ({ ...s })),
        notes: selectedEvent.notes
      });
    }
    setIsEditMode(false);
  };

  const handleDeleteEvent = async (id) => {
    const eventId = getEventIdentifier(typeof id === 'object' ? id : { id });
    const ok = await confirm('Are you sure you want to delete this event?');
    if (!ok) return;
    try {
      const token = session.getToken();
      if (token && eventId) {
        await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('event delete failed', e);
    }

    const next = events.filter(e => getEventIdentifier(e) !== eventId);
    setEvents(next);
    try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, next); } catch (e) {}
    broadcastEventsChanged();

    // If the deleted event is currently open in the view modal, close it
    if (selectedEvent && getEventIdentifier(selectedEvent) === eventId) {
      setShowViewModal(false);
      setSelectedEvent(null);
      setIsEditMode(false);
    }
  };

  const handleUpdateStatus = (id, newStatus) => {
    const next = events.map(e => (e.id === id ? { ...e, status: newStatus } : e));
    setEvents(next);
  try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, next); } catch (e) {}
    broadcastEventsChanged();
  };

  const handleWhatsApp = (event) => {
    // Open choice dialog: To Client (send directly) vs To Others (copy template)
    setClientShareTarget(event);
    setClientShareChannel('whatsapp');
  };

  const handleEmail = (event) => {
    // Open choice dialog: To Client (send directly) vs To Others (copy template)
    setClientShareTarget(event);
    setClientShareChannel('email');
  };

  const normalizeClientSharePhone = (raw) => {
    let phone = (raw || '').toString().replace(/\D/g, '');
    if (phone.length === 10) phone = '91' + phone;
    return phone;
  };

  const handleClientShareToClient = () => {
    if (!clientShareTarget) return;
    if (clientShareChannel === 'whatsapp') {
      const phone = normalizeClientSharePhone(clientShareTarget.clientPhone);
      if (!phone) {
        showToast('No phone number available for this client.');
        return;
      }
      const message = buildEventWhatsApp({ event: clientShareTarget, company, audience: 'client' });
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
    } else {
      const to = (clientShareTarget.clientEmail || '').trim();
      if (!to) {
        showToast('No email address available for this client.');
        return;
      }
      const { subject, body } = buildEventEmail({ event: clientShareTarget, company, audience: 'client' });
      window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  const handleClientShareOthersViaApp = () => {
    if (!clientShareTarget) return;
    if (clientShareChannel === 'whatsapp') {
      const message = buildEventWhatsApp({ event: clientShareTarget, company, audience: 'others' });
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    } else {
      const { subject, body } = buildEventEmail({ event: clientShareTarget, company, audience: 'others' });
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  const handleUpdateServiceStatus = (eventId, serviceId, newStatus) => {
    const next = events.map(e => {
      if (e.id === eventId) {
        const updatedServices = e.services.map(s =>
          s.id === serviceId ? { ...s, status: newStatus } : s
        );
        // Update event status based on services using derived rules
        const eventStatus = deriveEventStatusFromServices(updatedServices);
        return { ...e, services: updatedServices, status: eventStatus };
      }
      return e;
    });
    setEvents(next);
  try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, next); } catch (e) {}
    broadcastEventsChanged();
  };

  const addService = () => {
    // Prefill new service date with the selected quotation's main date,
    // falling back to the current schedule date (first service).
    const linkedQuotation = (formData.quotationRef || '') !== ''
      ? quotations.find(x => [x.id, x.quotationId, x.ref].some(v => v != null && String(v) === String(formData.quotationRef)))
      : null;
    const prefillDate = toInputDate(linkedQuotation?.mainEventDate)
      || (Array.isArray(formData.services) && formData.services.length > 0 ? (formData.services[0].date || '') : '');
    setFormData({
      ...formData,
      services: [
        {
          name: '',
          date: prefillDate,
          time: '',
          location: '',
          locationDetail: null,
          status: 'scheduled',
          notes: '',
          typeEditable: false
        },
        ...formData.services
      ]
    });
  };

  const removeService = (index) => {
    if (formData.services.length > 1) {
      setFormData({
        ...formData,
        services: formData.services.filter((_, i) => i !== index)
      });
    }
  };

  // `forceEditable` controls whether the name field should be shown as a free-text input
  // - true: force free-text input (datalist) mode
  // - false: force select mode
  // - undefined: don't change the existing `typeEditable` flag
  const updateService = (index, field, value, forceEditable) => {
    const newServices = [...formData.services];
    newServices[index][field] = value;
    // If location text is edited manually, drop the old map pin so we never share a wrong link.
    if (field === 'location') {
      newServices[index].locationDetail = null;
    }
    // Only change typeEditable when caller explicitly requests it. This prevents
    // switching from <select> -> free-text immediately after selecting a value,
    // which made it hard to re-open the select to change the selection.
    if (field === 'name' && forceEditable !== undefined) {
      newServices[index].typeEditable = !!forceEditable;
    }
    // If user manually edits a field that was previously marked as 'same as above', clear that flag
    if (index > 0) {
      // Clear the unified sameAsAbove flag when the user edits date/time/location manually
      if ((field === 'date' || field === 'time' || field === 'location') && newServices[index].sameAsAbove) newServices[index].sameAsAbove = false;
    }
    setFormData({ ...formData, services: newServices });
  };

  // Map-aware location update: keeps text + accurate map detail together.
  // Plain typing still works (detail = null) so old behaviour is untouched.
  const updateServiceLocation = (index, location, locationDetail) => {
    const newServices = [...formData.services];
    newServices[index].location = location;
    newServices[index].locationDetail = locationDetail || null;
    if (index > 0 && newServices[index].sameAsAbove) newServices[index].sameAsAbove = false;
    setFormData({ ...formData, services: newServices });
  };

  // Copy date/time/location from previous service into the given service index
  const copyServiceFromPrevious = (index, fields = ['date','time','location']) => {
    if (index <= 0) return;
    const prev = formData.services[index - 1] || {};
    const newServices = [...formData.services];
    fields.forEach(f => {
      if (prev[f] !== undefined) newServices[index][f] = prev[f];
    });
    // Keep the map pin in sync when location is copied
    if (fields.includes('location')) {
      newServices[index].locationDetail = prev.locationDetail || null;
      if (prev.locationMapEnabled !== undefined) newServices[index].locationMapEnabled = prev.locationMapEnabled;
    }
    // Mark unified flag when copying
    newServices[index].sameAsAbove = true;
    setFormData({ ...formData, services: newServices });
  };

  const toggleSameAsAbove = (index, checked) => {
    if (index <= 0) return;
    const newServices = [...formData.services];
    newServices[index].sameAsAbove = checked;
    if (checked) {
      // copy date/time/location from previous
      const prev = formData.services[index - 1] || {};
      ['date', 'time', 'location'].forEach(f => {
        if (prev[f] !== undefined) newServices[index][f] = prev[f];
      });
      // keep map pin in sync with copied location
      newServices[index].locationDetail = prev.locationDetail || null;
      if (prev.locationMapEnabled !== undefined) newServices[index].locationMapEnabled = prev.locationMapEnabled;
    }
    setFormData({ ...formData, services: newServices });
  };

  const handleSubmitEvent = async () => {
    // Save any pending new services to database first
    if (pendingNewServices.length > 0) {
      const savedServices = [];
      for (const pendingService of pendingNewServices) {
        // Events don't have price in items, so we use 0 as basePrice
        // (Events reference services by name only, not price)
        const fullService = {
          id: pendingService.id,
          name: pendingService.name,
          category: 'Uncategorized',
          description: '',
          basePrice: 0,
          deliverables: [],
          features: [],
        };
        const saved = await savePendingServiceToDatabase(fullService);
        if (saved) {
          savedServices.push(saved);
        }
      }
      
      // Update availableServices with newly saved services
      if (savedServices.length > 0) {
        setAvailableServices(prev => [...prev, ...savedServices]);
        // Clear pending services as they're now saved
        setPendingNewServices([]);
      }
    }

    const newEvent = {
      id: formData.quotationId,
      quotationRef: formData.quotationRef || '', // Store quotation reference
      clientName: formData.clientName,
      clientEmail: formData.clientEmail,
      clientPhone: formData.clientPhone,
      orderType: formData.orderType,
      invitedBy: formData.orderType === 'suborder' ? String(formData.invitedBy || formData.referredBy || '').trim() : '',
      referredBy: formData.orderType === 'suborder' ? String(formData.invitedBy || formData.referredBy || '').trim() : '',
      notifyAssignees: formData.notifyAssignees !== false,
      team: Array.isArray(formData.team) ? formData.team.filter(Boolean) : [],
      services: formData.services.map((s, idx) => {
        const { sameAsDateAbove, sameAsTimeAbove, sameAsLocationAbove, sameAsAbove, typeEditable, ...rest } = s || {};
        return {
          id: Date.now() + idx,
          ...rest
        };
      }),
      // Derive top-level event status from services rather than relying solely on formData.eventStatus
      status: deriveEventStatusFromServices(formData.services.map(s => ({ ...(s || {}) }))),
  // Store full ISO datetime so time formatting works correctly in views
  createdDate: new Date().toISOString(),
      // Top-level date/time mirrors the first service when one has a date.
      // Undated events keep '' — never auto-stamp today (the calendar and
      // dashboard treat '' as TBA and leave the event off the grid).
  date: (formData.services && formData.services.length > 0 ? formData.services[0].date : '') || '',
      time: (formData.services && formData.services.length > 0 ? formData.services[0].time : '') || '',
      notes: formData.notes,
      reminderEnabled: createEventReminderEnabled,
      };

  // Prevent adding duplicates: remove any existing event(s) with the same id
  const next = [newEvent, ...events.filter(e => e && e.id !== newEvent.id)];
    setEvents(next);
  try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, next); } catch (e) {}
  broadcastEventsChanged();

  // Try to persist to server when authenticated. If it fails, keep local copy.
  try {
    const token = session.getToken();
    if (token) {
      const resp = await fetch(`${API_BASE_URL}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newEvent),
      });

      if (resp.ok) {
        const created = await resp.json();
        // Replace local provisional event with server-saved event (match by id)
        const replaced = [created, ...events.filter(e => e && e.id !== created.id)];
        setEvents(replaced);
        try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, replaced); } catch (e) {}
        broadcastEventsChanged();
        const notifiedCount = created?._assignment?.notified?.length || 0;
        const nonMivent = Array.isArray(created?._assignment?.nonMivent) ? created._assignment.nonMivent : [];
        if (nonMivent.length > 0) {
          // Some assignees are not Mivent users -> prompt manual share
          setManualShareTargets(nonMivent);
          setManualShareEvent(created);
          setShowManualShareModal(true);
          const names = nonMivent.map(n=>n.name).join(", ");
          showToast(`Event created. ${notifiedCount} Mivent user(s) notified. ${names} not on Mivent — please share details manually.`, { type:"warning", duration:5000 });
        } else {
          // success toasts suppressed — notification only (warning for manual share kept as it requires action)
        }

      } else {
        // If server rejected, surface a toast but keep local state
        const text = await resp.text().catch(() => '');
        showToast(`Unable to save event to server: ${resp.status} ${resp.statusText} ${text}`, { type:"error" });
      }
    }
  } catch (e) {
    // network or unexpected error — keep local copy and notify user
    // eslint-disable-next-line no-console
    console.warn('Failed to persist event to server', e);
    showToast('Event saved locally but failed to sync with server.');
  }

    // Dispatch notification for new event if user has enabled it; instant server sync
    if (notifications?.events) {
      try {
        dispatch(addNotification({
          title: 'Event created',
          message: `${newEvent.id} — ${newEvent.clientName || 'No client'}`,
          type: 'info',
          timestamp: new Date().toISOString(),
          path: '/events',
          data: { openViewId: newEvent.id }
        }));
      } catch (e) {}
    }
    try { window.dispatchEvent(new CustomEvent('notifications:refresh')); } catch (e) {}
    try { setTimeout(() => window.dispatchEvent(new CustomEvent('notifications:refresh')), 600); } catch (e) {}
    try { setTimeout(() => window.dispatchEvent(new CustomEvent('notifications:refresh')), 1600); } catch (e) {}
    setShowCreateModal(false);
    // Clear any saved create-draft
    try { removeDraftFromSession(makeDraftKey()); } catch (e) {}
  };

  const handleUpdateEvent = async () => {
    if (!isValidName(formData.clientName)) {
      showToast('Please enter a valid client name');
      return;
    }
    if (!isValidPhone(formData.clientPhone)) {
      showToast('Please enter a valid 10-digit phone number (numbers only)');
      return;
    }
    if (!isValidEmail(formData.clientEmail)) {
      showToast('Please enter a valid email address');
      return;
    }
    const selectedEventId = getEventIdentifier(selectedEvent);

    const next = events.map(e =>
      getEventIdentifier(e) === selectedEventId
        ? {
            ...e,
            quotationRef: formData.quotationRef || '',
            orderType: formData.orderType,
            invitedBy: formData.orderType === 'suborder' ? String(formData.invitedBy || formData.referredBy || '').trim() : '',
            referredBy: formData.orderType === 'suborder' ? String(formData.invitedBy || formData.referredBy || '').trim() : '',
            notifyAssignees: formData.notifyAssignees !== false,
            clientName: formData.clientName,
            clientEmail: formData.clientEmail,
            clientPhone: formData.clientPhone,
            // allow explicit top-level status from the edit form; fallback to derived status from services
            status: (formData.eventStatus || deriveEventStatusFromServices(formData.services.map(s => ({ ...(s || {}) })))),
            team: Array.isArray(formData.team) ? formData.team.filter(Boolean) : [],
            services: formData.services.map((s) => {
              const { sameAsDateAbove, sameAsTimeAbove, sameAsLocationAbove, sameAsAbove, typeEditable, ...rest } = s || {};
              return rest;
            }),
            // Keep top-level date/time in sync with first service when possible.
            // Undated stays '' (TBA) — never fall back to createdDate/today,
            // otherwise a dateless edit would pop onto a calendar day.
            date: (formData.services && formData.services.length > 0 ? formData.services[0].date : e.date) || '',
            time: (formData.services && formData.services.length > 0 ? formData.services[0].time : e.time) || '',
            notes: formData.notes,
            reminderEnabled: isEventReminderEnabled,
          }
        : e
    );
    setEvents(next);
  try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, next); } catch (e) {}
  // Broadcast optimistically so Dashboard + calendars update instantly,
  // even before the server round-trip finishes. A second broadcast follows
  // the server confirmation below.
  broadcastEventsChanged();

  // Try to persist update to server
  try {
    const token = session.getToken();
      if (token && selectedEventId) {
        const resp = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(selectedEventId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
          body: JSON.stringify(next.find(x => getEventIdentifier(x) === selectedEventId)),
      });
      if (resp.ok) {
        const updated = await resp.json();
          // Map over the optimistic `next` (not the stale `events` closure)
          // so local edits are preserved and the server doc wins by id.
          const replaced = next.map(ev => getEventIdentifier(ev) === getEventIdentifier(updated) ? normalizeEventRecord(updated) : ev);
        setEvents(replaced);
        try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, replaced); } catch (e) {}
          broadcastEventsChanged();
        const updNotified = updated?._assignment?.notified?.length || 0;
        const updNonMivent = Array.isArray(updated?._assignment?.nonMivent) ? updated._assignment.nonMivent : [];
        if (updNonMivent.length > 0 && updNotified === 0) {
          // pure manual share needed for newly added non-Mivent members
          setManualShareTargets(updNonMivent);
          setManualShareEvent(updated);
          setShowManualShareModal(true);
          showToast(`${updNonMivent.map(n=>n.name).join(", ")} not on Mivent — please share manually`, { type:"warning", duration:5000 });
        } else if (updNotified > 0 && updNonMivent.length > 0) {
          setManualShareTargets(updNonMivent);
          setManualShareEvent(updated);
          setShowManualShareModal(true);
          showToast(`Event updated. ${updNotified} notified. ${updNonMivent.map(n=>n.name).join(", ")} needs manual share`, { type:"warning", duration:5000 });
        } else if (updNotified > 0) {
          showToast(`Event updated & ${updNotified} team member(s) notified`, { type:"success" });
        } else {
          showToast('Event updated successfully', { type: 'success' });
        }

      } else {
        const txt = await resp.text().catch(() => '');
        showToast(`Unable to update event on server: ${resp.status} ${resp.statusText} ${txt}`, { type:"error" });
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to update event on server', e);
    showToast('Event updated locally but failed to sync with server.');
  }

    setIsEditMode(false);
    setShowViewModal(false);
    setSelectedEvent(null);
    // Clear any saved edit-draft for this event
    try { removeDraftFromSession(makeDraftKey(selectedEvent && selectedEvent.id)); } catch (e) {}
  };

  // Normalize existing events on mount: ensure top-level date/time fields exist (dashboard uses them)
  // Undated events keep date '' (TBA) — never backfill createdDate/today.
  useEffect(() => {
    try {
      if (!Array.isArray(events) || events.length === 0) return;
      const normalized = events.map(ev => {
        const firstSvc = Array.isArray(ev.services) && ev.services.length > 0 ? ev.services[0] : null;
        const derivedDate = ev.date || (firstSvc && firstSvc.date) || '';
        const derivedTime = ev.time || (firstSvc && firstSvc.time) || '';
        // Ensure orderType exists - treat missing as 'own'
        const orderType = ev.orderType || 'own';
        // Ensure top-level status matches services-derived status
        const derivedStatus = deriveEventStatusFromServices(ev.services || []);
        if (ev.date !== derivedDate || ev.time !== derivedTime || ev.orderType !== orderType || ev.status !== derivedStatus) {
          return { ...ev, date: derivedDate, time: derivedTime, orderType, status: derivedStatus };
        }
        return ev;
      });
      // If any change detected, persist normalized events
      const changed = JSON.stringify(normalized) !== JSON.stringify(events);
      if (changed) {
        setEvents(normalized);
  try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, normalized); } catch (e) {}
        broadcastEventsChanged();
      }
    } catch (e) {
      // ignore
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const pendingOpenRef = useRef(null);

  const openTeamPaymentModal = (memberName, eventObj = null) => {
    const targetMember = String(memberName || '').trim();
    if (!targetMember) return;
    const eventId = eventObj ? getEventIdentifier(eventObj) : '';
    const eventName = eventObj ? (eventObj.clientName || `Event ${eventObj.quotationId || eventObj.id || eventId}`) : '';
    const matched = fullTeamMembers.find(m => String(m?.name||'').trim().toLowerCase() === targetMember.toLowerCase()) || null;
    setPaymentMemberName(targetMember);
    setPaymentMemberObj(matched);
    setUseCustomEventName(false);
    setPaymentForm({
      eventId: eventId || '',
      eventName: eventName || '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      status: 'pending',
      notes: ''
    });
    setPaymentErrors({});
    setEditingPaymentId(null);
    setShowTeamPaymentModal(true);
  };
  const closeTeamPaymentModal = () => {
    setShowTeamPaymentModal(false);
    setPaymentMemberName('');
    setPaymentMemberObj(null);
    setUseCustomEventName(false);
    setPaymentErrors({});
    setEditingPaymentId(null);
  };

  const handleRemoveTeamMemberFromView = async (memberName) => {
    if (!selectedEvent) return;
    const target = String(memberName || '').trim();
    if (!target) return;
    const eventId = getEventIdentifier(selectedEvent);
    const ok = await confirm(`Remove ${target} from this event?`);
    if (!ok) return;
    const nextTeam = (Array.isArray(selectedEvent.team) ? selectedEvent.team : [])
      .filter((m) => String(m || '').trim().toLowerCase() !== target.toLowerCase());
    const updatedEvent = { ...selectedEvent, team: nextTeam };
    setSelectedEvent(updatedEvent);
    setFormData((prev) => ({ ...prev, team: [...nextTeam] }));
    setEvents((prev) => {
      const next = prev.map((e) =>
        getEventIdentifier(e) === eventId ? { ...e, team: [...nextTeam] } : e
      );
      try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, next); } catch (err) {}
      return next;
    });
    try {
      const token = session.getToken();
      if (token && eventId) {
        const resp = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedEvent),
        });
        if (resp.ok) {
          const updated = await resp.json();
          const normalized = normalizeEventRecord(updated);
          const finalTeam = Array.isArray(normalized.team) ? normalized.team : nextTeam;
          setSelectedEvent((prev) => (prev ? { ...prev, team: [...finalTeam] } : prev));
          setFormData((prev) => ({ ...prev, team: [...finalTeam] }));
          setEvents((prev) => {
            const replaced = prev.map((ev) =>
              getEventIdentifier(ev) === getEventIdentifier(normalized) ? normalized : ev
            );
            try { storage.setJSONAndSync(STORAGE_KEYS.EVENTS, replaced); } catch (err) {}
            return replaced;
          });
          broadcastEventsChanged();
          showToast(`${target} removed from event`, { type: 'success' });
        } else {
          const txt = await resp.text().catch(() => '');
          showToast(`Removed locally but failed to sync: ${resp.status} ${txt}`, { type: 'error' });
        }
      } else {
        broadcastEventsChanged();
        showToast(`${target} removed from event`, { type: 'success' });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to remove team member on server', e);
      showToast('Removed locally but failed to sync with server.');
    }
  };

  // Ping / resend assignment notification to team members from the View modal.
  // memberName (optional): ping a single member; otherwise pings the whole team.
  const handlePingAssignees = async (memberName) => {
    if (!selectedEvent || pingLoading) return;
    const target = String(memberName || '').trim();
    const eventId = getEventIdentifier(selectedEvent);
    if (!eventId) { showToast('Event identifier missing', { type: 'error' }); return; }
    const team = Array.isArray(selectedEvent.team) ? selectedEvent.team.filter(Boolean) : [];
    if (team.length === 0) { showToast('No team members assigned to this event', { type: 'warning' }); return; }
    // Confirm before notifying — pings send real push/in-app notifications.
    const ok = target
      ? await confirm(`Send notification to ${target}?`, { title: 'Ping Member', confirmText: 'Send', cancelText: 'Cancel', showWarning: false })
      : await confirm(`Send notification to all ${team.length} team member${team.length === 1 ? '' : 's'}?`, { title: 'Ping Team', confirmText: 'Send All', cancelText: 'Cancel', showWarning: false });
    if (!ok) return;
    setPingLoading(true);
    if (target) setPingMember(target);
    try {
      const token = session.getToken();
      if (!token) { showToast('Please login to send notifications', 'error'); return; }
      const resp = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(target ? { members: [target] } : {}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        showToast(data.message || 'Failed to send notification', { type: 'error' });
        return;
      }
      const notified = Array.isArray(data?._assignment?.notified) ? data._assignment.notified : [];
      const nonMivent = Array.isArray(data?._assignment?.nonMivent) ? data._assignment.nonMivent : [];
      if (nonMivent.length > 0) {
        // Non-Mivent members can't receive in-app notification — offer manual share
        setManualShareTargets(nonMivent);
        setManualShareEvent(data?.id ? normalizeEventRecord(data) : selectedEvent);
        setShowManualShareModal(true);
      }
      if (notified.length > 0) {
        const names = notified.map((n) => n.name).filter(Boolean).join(', ');
        showToast(target ? `Notification resent to ${target}` : `Notification sent to ${notified.length} member(s)${names ? `: ${names}` : ''}`, { type: 'success' });
      } else if (nonMivent.length === 0) {
        showToast(target ? `${target} could not be notified (not on Mivent or no email)` : 'No members notified (not on Mivent or missing email)', { type: 'warning', duration: 5000 });
      } else {
        showToast(`${nonMivent.map((n) => n.name).join(', ')} not on Mivent — please share manually`, { type: 'warning', duration: 5000 });
      }
      try { window.dispatchEvent(new CustomEvent('notifications:refresh')); } catch (e) {}
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to ping assignees', e);
      showToast('Failed to send notification', { type: 'error' });
    } finally {
      setPingLoading(false);
      setPingMember('');
    }
  };

  useEffect(() => {
    if (location?.state?.openCreate) {
      // Open create modal (no draft loading from storage)
      handleCreateEvent();
      try { window.history.replaceState({}, document.title); } catch (e) {}
    }

    // Helper: resolve deep-link id from either location.state (in-app navigation)
    // or ?id= query param (push click when app was closed → SW opens /events?id=...)
    const getDeepLinkId = () => {
      if (location?.state?.openViewId) return String(location.state.openViewId);
      try {
        const params = new URLSearchParams(window.location.search);
        const qId = params.get('id');
        if (qId) return String(qId);
      } catch (e) {}
      return null;
    };

    const idToOpen = getDeepLinkId();
    if (idToOpen) {
      const evt = events.find(e => {
        const ids = [e.id, e._id, e.quotationId].map(v => v ? String(v) : '');
        return ids.includes(idToOpen);
      });
      if (evt) {
        handleViewEvent(evt);
      } else {
        // Try shared/assigned event for B
        openSharedEvent(idToOpen);
        pendingOpenRef.current = idToOpen;
      }
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('id')) {
          url.searchParams.delete('id');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else {
          window.history.replaceState({}, document.title);
        }
      } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle push deep-link navigation when app is already open (SW calls client.navigate)
  // React Router updates location.search without remounting — watch it.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId && !pendingOpenRef.current) {
        const evt = events.find(e => {
          const ids = [e.id, e._id, e.quotationId].map(v => v ? String(v) : '');
          return ids.includes(String(qId));
        });
        if (evt) {
          handleViewEvent(evt);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else {
          pendingOpenRef.current = String(qId);
          openSharedEvent(String(qId));
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // If a pending open id was set (because events weren't loaded when navigation happened),
  // try to open it now that `events` may have been populated.
  useEffect(() => {
    if (pendingOpenRef.current) {
      const idToOpen = String(pendingOpenRef.current);
      const evt = events.find(e => {
        const ids = [e.id, e._id, e.quotationId].map(v => v ? String(v) : '');
        return ids.includes(idToOpen);
      });
      if (evt) {
        handleViewEvent(evt);
        pendingOpenRef.current = null;
        try { window.history.replaceState({}, document.title); } catch (e) {}
      } else {
        // Check if it's a shared event - try fetch once
        // Don't loop: clear after one attempt to avoid spam
        const attemptId = idToOpen;
        pendingOpenRef.current = null;
        openSharedEvent(attemptId);
      }
    }
  }, [events]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const toggleColumn = (columnKey) => {
    // NOTE: never dispatch inside a setState updater — updaters must be pure
    // (dispatching there warns "Cannot update a component (Navbar) while
    // rendering a different component (Event)").
    const next = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey],
    };
    setVisibleColumns(next);
    dispatch(updateColumnPreferences({ events: next }));
    dispatch(saveColumnPreferences({ events: next }));
  };

  const toggleAllColumns = (checked) => {
    const newVisibleColumns = {};
    columnOptions.forEach(column => {
      newVisibleColumns[column.key] = checked;
    });
    const next = { ...EVENT_COLUMN_DEFAULTS, ...newVisibleColumns };
    setVisibleColumns(next);
    dispatch(updateColumnPreferences({ events: next }));
    dispatch(saveColumnPreferences({ events: next }));
  };

  const isAllColumnsSelected = () => {
    return columnOptions.every(column => visibleColumns[column.key]);
  };

  const columnOptions = [
    { key: 'id', label: t('event.colEventId') },
    { key: 'quotationNo', label: t('event.colQuotNo') },
    { key: 'date', label: t('common.date') },
    { key: 'services', label: t('common.services') },
    { key: 'orderType', label: t('common.orderType') },
    { key: 'invitedBy', label: t('event.colInvitedBy') || t('event.colReferredBy') },
    { key: 'team', label: t('event.colTeam') },
    { key: 'location', label: t('common.location') },
    { key: 'status', label: t('common.status') }
  ];

  // Filter events for stats (excludes status filter to show all status counts)
  const getFilteredEventsForStats = () => {
    const q = (searchQuery || '').toString().trim().toLowerCase();
    let filtered = events.filter(event => {
      const clientName = (event?.clientName || '').toString().toLowerCase();
      const quotationRef = (event?.quotationRef || '').toString().toLowerCase();

      const matchesName = q === '' || clientName.includes(q);

      const matchesDate = (event.services || []).some(s => {
        const sdate = (s?.date || '').toString().toLowerCase();
        const slocal = s?.date ? formatDateDisplay(s.date).toLowerCase() : '';
        return sdate.includes(q) || slocal.includes(q);
      });

      const matchesLocation = (event.services || []).some(s => (s?.location || '').toString().toLowerCase().includes(q));

      const matchesId = (event?.id || '').toString().toLowerCase().includes(q);

      const matchesQuotation = quotationRef.includes(q);

      const matchesServiceName = (event.services || []).some(s => (s?.name || '').toString().toLowerCase().includes(q));

      // Team: event.team may be an array of strings or objects with name
      const teamArr = Array.isArray(event.team) ? event.team : [];
      const teamText = teamArr.map(t => (typeof t === 'string' ? t : (t.name || ''))).join(' ').toLowerCase();
      const matchesTeam = teamText.includes(q);

      const invitedByText = getInvitedBy(event).toLowerCase();
      const matchesInvitedBy = q === '' || invitedByText.includes(q);

      const matchesSearch = matchesName || matchesDate || matchesLocation || matchesId || matchesServiceName || matchesQuotation || matchesTeam || matchesInvitedBy;
      
      // Order type filter (but NOT status filter for stats)
      const matchesOrderType = orderTypeFilter === 'all' || (event.orderType || 'own') === orderTypeFilter;

      return matchesSearch && matchesOrderType;
    });

    // Apply date range filter if set (dateFrom/dateTo are YYYY-MM-DD)
    if (dateFrom || dateTo) {
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;

      filtered = filtered.filter(ev => {
        // An event matches the date range if ANY of its services have a date within the range
        const anyInRange = (ev.services || []).some(s => {
          if (!s || !s.date) return false;
          const sd = new Date(s.date);
          if (Number.isNaN(sd.getTime())) return false;
          if (from && sd < from) return false;
          if (to && sd > to) return false;
          return true;
        });
        return anyInRange;
      });
    }

    return filtered;
  };

  const getFilteredEvents = () => {
    const q = (searchQuery || '').toString().trim().toLowerCase();
    let filtered = events.filter(event => {
      const clientName = (event?.clientName || '').toString().toLowerCase();
      const quotationRef = (event?.quotationRef || '').toString().toLowerCase();

      const matchesName = q === '' || clientName.includes(q);

      const matchesDate = (event.services || []).some(s => {
        const sdate = (s?.date || '').toString().toLowerCase();
        const slocal = s?.date ? formatDateDisplay(s.date).toLowerCase() : '';
        return sdate.includes(q) || slocal.includes(q);
      });

      const matchesLocation = (event.services || []).some(s => (s?.location || '').toString().toLowerCase().includes(q));

      const matchesId = (event?.id || '').toString().toLowerCase().includes(q);

      const matchesQuotation = quotationRef.includes(q);

      const matchesServiceName = (event.services || []).some(s => (s?.name || '').toString().toLowerCase().includes(q));

      // Team: event.team may be an array of strings or objects with name
      const teamArr = Array.isArray(event.team) ? event.team : [];
      const teamText = teamArr.map(t => (typeof t === 'string' ? t : (t.name || ''))).join(' ').toLowerCase();
      const matchesTeam = teamText.includes(q);

      const invitedByText = getInvitedBy(event).toLowerCase();
      const matchesInvitedBy = q === '' || invitedByText.includes(q);

      const matchesSearch = matchesName || matchesDate || matchesLocation || matchesId || matchesServiceName || matchesQuotation || matchesTeam || matchesInvitedBy;
      const matchesStatus = filterStatus === 'all' || event.status === filterStatus || !!statFilter;
      const matchesStat = !statFilter || (() => {
        if (statFilter === 'scheduled') return event.status === 'scheduled';
        return event.status === statFilter;
      })();

      // Order type filter
      const matchesOrderType = orderTypeFilter === 'all' || (event.orderType || 'own') === orderTypeFilter;

      return matchesSearch && matchesStatus && matchesOrderType && matchesStat;
    });

    // Apply date range filter if set (dateFrom/dateTo are YYYY-MM-DD)
    if (dateFrom || dateTo) {
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;

      filtered = filtered.filter(ev => {
        // An event matches the date range if ANY of its services have a date within the range
        const anyInRange = (ev.services || []).some(s => {
          if (!s || !s.date) return false;
          const sd = new Date(s.date);
          if (Number.isNaN(sd.getTime())) return false;
          if (from && sd < from) return false;
          if (to && sd > to) return false;
          return true;
        });
        return anyInRange;
      });
    }

    // Sort the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'client':
          comparison = a.clientName.localeCompare(b.clientName);
          break;
        case 'date':
          const dateA = a.services.length > 0 ? new Date(a.services[0].date) : new Date(0);
          const dateB = b.services.length > 0 ? new Date(b.services[0].date) : new Date(0);
          comparison = dateA - dateB;
          break;
        // 'amount' sorting removed (column not displayed)
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#fbbf24';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <FaClock />;
      case 'completed': return <FaCheckCircle />;
      case 'cancelled': return <FaTimes />;
      default: return <FaClock />;
    }
  };

  const deriveEventStatusFromServices = (services) => {
    if (!Array.isArray(services) || services.length === 0) return 'scheduled';
    const normalized = services.map(s => (s && s.status) ? s.status : 'scheduled');
    const allCancelled = normalized.every(st => st === 'cancelled');
    if (allCancelled) return 'cancelled';
    const allCompletedOrCancelled = normalized.every(st => st === 'completed' || st === 'cancelled');
    if (allCompletedOrCancelled) return 'completed';
    const anyScheduled = normalized.some(st => st === 'scheduled');
    if (anyScheduled) return 'scheduled';
    return 'scheduled';
  };

  // Format order type for display: e.g. 'own' -> 'Own', 'suborder' -> 'Suborder'
  const formatOrderType = (val) => {
    if (!val) return 'N/A';
    return val.toString().split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Keep dropdown filter and stat cards directly proportional (bidirectional sync).
  // 'all' <-> Total (statFilter null); otherwise both hold the same status value.
  const toggleStatFilter = (status) => {
    setStatFilter((prev) => {
      const next = prev === status ? null : status;
      setFilterStatus(next === null ? 'all' : next);
      return next;
    });
  };

  const resetStatFilter = () => {
    setStatFilter(null);
    setFilterStatus('all');
  };

  const handleFilterStatusChange = (status) => {
    setFilterStatus(status);
    setStatFilter(status === 'all' ? null : status);
  };

  const filteredEvents = getFilteredEvents();

  // Pagination logic
  const totalPages = Math.ceil(filteredEvents.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedEvents = filteredEvents.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, statFilter, orderTypeFilter, dateFrom, dateTo, sortBy, sortOrder]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Return a 1-based serial number for a given event id based on the current
  // filteredEvents ordering. Falls back to the full events list if not found
  // in the filtered set (e.g. when a filter/search excludes it).
  const getSerialNumber = (id) => {
    const targetId = getEventIdentifier(typeof id === 'object' ? id : { id });
    const idx = filteredEvents.findIndex(e => getEventIdentifier(e) === targetId);
    if (idx !== -1) return idx + 1;
    const idxAll = events.findIndex(e => getEventIdentifier(e) === targetId);
    return idxAll !== -1 ? idxAll + 1 : '';
  };

  const handleExportEvents = async () => {
    try {
      const confirmed = await confirm('Export events to CSV?', {
        title: 'Export Events',
        confirmText: 'Export',
        cancelText: 'Cancel',
        showWarning: false
      });
      if (!confirmed) return;
      const rows = (Array.isArray(filteredEvents) && filteredEvents.length > 0) ? filteredEvents : events;
      if (!rows || rows.length === 0) { showToast('No events to export'); return; }

      // Base columns always included
      const headers = ['Sl. No', 'Client Name', 'Phone', 'Mail'];

      // Append visible columns from columnOptions in order
      columnOptions.forEach(col => {
        if (visibleColumns[col.key]) headers.push(col.label);
      });

      const esc = (v) => { const s = v === undefined || v === null ? '' : String(v); return '"' + s.replace(/"/g, '""') + '"'; };

      const lines = rows.map((ev, idx) => {
        const sn = getSerialNumber(ev.id) || (idx + 1);
        const base = [sn, ev.clientName || '', ev.clientPhone || '', ev.clientEmail || ''];

        columnOptions.forEach(col => {
          if (!visibleColumns[col.key]) return;
          switch (col.key) {
            case 'id':
              base.push(getEventIdentifier(ev) || '');
              break;
            case 'quotationNo':
              base.push(ev.quotationRef || ev.id || '');
              break;
            case 'date': {
              if (Array.isArray(ev.services) && ev.services.length > 0) {
                const sorted = ev.services.slice().sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
                base.push(sorted.map(s => s && s.date ? formatDateDisplay(s.date) : '').filter(Boolean).join('; '));
              } else {
                base.push('');
              }
              break;
            }
            case 'services': {
              if (Array.isArray(ev.services) && ev.services.length > 0) {
                const sorted = ev.services.slice().sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
                base.push(sorted.map(s => s && s.name ? s.name : s || '').filter(Boolean).join('; '));
              } else {
                base.push('');
              }
              break;
            }

            case 'team':
              base.push(Array.isArray(ev.team) ? ev.team.filter(Boolean).join('; ') : '');
              break;
            case 'location': {
              if (Array.isArray(ev.services) && ev.services.length > 0) {
                const sorted = ev.services.slice().sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
                const locs = sorted.map(s => s && s.location ? s.location : '').filter(Boolean);
                base.push(Array.from(new Set(locs)).join('; '));
              } else {
                base.push(ev.location || '');
              }
              break;
            }
            case 'orderType':
              base.push(formatOrderType(ev.orderType || 'own'));
              break;
            case 'invitedBy':
            case 'referredBy': {
              const isSub = String(ev.orderType || 'own').toLowerCase() === 'suborder';
              base.push(isSub ? getInvitedBy(ev) : 'NA');
              break;
            }
            case 'status':
              base.push(ev.status || '');
              break;
            default:
              base.push('');
          }
        });

        return base.map(esc).join(',');
      });

      const csv = [headers.map(esc).join(','), ...lines].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `events_export_${formatDateISO(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { console.error('export error', e); showToast('Unable to export events'); }
  };

  return (
    <div className="event">
      {/* Header */}
      <PageHero
        icon={<FaCalendarCheck />}
        title={t('pages.events')}
        subtitle={t('pages.eventsSubtitle')}
        actions={
          <>
            <button
              className="page-hero__btn"
              onClick={handleExportEvents}
              title="Export Events"
            >
              <FaShareSquareIcon /> Export
            </button>

            <button className="page-hero__btn" onClick={handleCreateEvent}>
              <FaPlus />
              New Event
            </button>
          </>
        }
      />

      {/* Stats */}
      <div className="event__stats">
        {(() => {
          const eventsForStats = getFilteredEventsForStats();
          const total = eventsForStats.length;
          const completed = eventsForStats.filter(e => e.status === 'completed').length;
          const cancelled = eventsForStats.filter(e => e.status === 'cancelled').length;
          const scheduled = eventsForStats.filter(e => e.status === 'scheduled').length;

          const items = [
            { key: 'total', label: 'Total Events', value: total, icon: <FaThLarge />, mod: 'total' },
            { key: 'completed', label: 'Completed', value: completed, icon: <FaCheck />, mod: 'completed' },
            { key: 'cancelled', label: 'Cancelled', value: cancelled, icon: <FaTimes />, mod: 'cancelled' },
            { key: 'scheduled', label: 'Scheduled', value: scheduled, icon: <FaClock />, mod: 'scheduled' }
          ];

          return items.map(item => {
            const isActive = item.key === 'total' ? !statFilter : statFilter === item.key;
            return (
              <div
                key={item.key}
                className={`event__stat-card${isActive ? ' event__stat-card--active' : ''}`}
                onClick={() => item.key === 'total' ? resetStatFilter() : toggleStatFilter(item.key)}
                style={{ cursor: 'pointer' }}
                role="button"
                aria-label={item.label}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.key === 'total' ? resetStatFilter() : toggleStatFilter(item.key); } }}
              >
                <div className={`event__stat-icon event__stat-icon--${item.mod}`}>
                  {item.icon}
                </div>
                <div className="event__stat-info">
                  <h3>{item.value}</h3>
                  <p>{item.label}</p>
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Toolbar */}
      <div className="event__toolbar">
        <div className="event__search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search events by client, ID, service or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="event__toolbar-right">
          <div className="event__filter">
            <FaFilter />
            <CustomSelect
              value={filterStatus}
              onChange={handleFilterStatusChange}
              options={[
                { value: 'all', label: t('common.allStatus') },
                { value: 'scheduled', label: t('event.statusScheduled') },
                { value: 'completed', label: t('event.statusCompleted') },
                { value: 'cancelled', label: t('event.statusCancelled') }
              ]}
            />
          </div>
          {/* Order Type Filter */}
          <div className="event__filter event__filter--order-type">
            <label>{t('event.orderLabel')}</label>
            <CustomSelect
              value={orderTypeFilter}
              onChange={setOrderTypeFilter}
              options={[
                { value: 'all', label: t('common.all') },
                { value: 'own', label: t('common.own') },
                { value: 'suborder', label: t('common.suborder') }
              ]}
            />
          </div>
          {/* Date Range Filter */}
          <div className="event__filter event__filter--date-range">
            <div className="event__date-input">
              <label>{t('common.from')}</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="event__date-input">
              <label>{t('common.toDate')}</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
          <div className="event__view-toggle">
            {viewMode === 'table' && (
              <div className="event__column-selector">
                <button
                  ref={eventBtnRef}
                  className="event__column-btn"
                  type="button"
                  onClick={() => setShowColumnMenu(!showColumnMenu)}
                  title="Column Preferences"
                >
                  <FaColumns />
                </button>
                {showColumnMenu && (
                  <div ref={eventMenuRef} className="event__column-menu">
                    <div className="event__column-menu-header">
                      <span>{t('common.showColumns')}</span>
                      <button type="button" onClick={() => setShowColumnMenu(false)}>
                        <FaTimes />
                      </button>
                    </div>
                    <div className="event__column-menu-items">
                      <label className="event__column-menu-item event__column-menu-item--select-all">
                        <input
                          type="checkbox"
                          checked={isAllColumnsSelected()}
                          onChange={(e) => toggleAllColumns(e.target.checked)}
                        />
                        {isAllColumnsSelected() ? <FaCheckSquare /> : <FaSquare />}
                        <span><strong>{t('common.selectAll')}</strong></span>
                      </label>
                      <div className="event__column-menu-divider"></div>
                      {columnOptions.map(column => (
                        <label key={column.key} className="event__column-menu-item">
                          <input
                            type="checkbox"
                            checked={visibleColumns[column.key]}
                            onChange={() => toggleColumn(column.key)}
                          />
                          {visibleColumns[column.key] ? <FaCheckSquare /> : <FaSquare />}
                          <span>{column.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              className={`event__view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <FaList />
            </button>
            <button
              className={`event__view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <FaThLarge />
            </button>
          </div>
        </div>
      </div>

      {/* Events Content */}
      <div className="event__content">
        {filteredEvents.length === 0 ? (
          <div className="event__empty">
            <FaCalendarAlt />
            <h3>{t('event.emptyTitle')}</h3>
            <p>{t('event.emptyHint')}</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="event__table-container">
            <table className="event__table">
              <thead>
                <tr>
                  <th className="event__col-no">{t('common.noCol')}</th>
                  {visibleColumns.id && <th className="event__col-id">{t('event.colEventId')}</th>}
                  {visibleColumns.orderType && <th className="event__col-order-type">{t('common.orderType')}</th>}
                  <th 
                    className="event__col-client event__sortable-header"
                    onClick={() => handleSort('client')}
                  >
                    {t('common.client')} {' '}
                    {sortBy === 'client' ? (
                      sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort className="event__sort-icon--muted" />
                    )}
                  </th>
                  {visibleColumns.quotationNo && <th className="event__col-quotation-no">{t('event.colQuotNo')}</th>}
                  {visibleColumns.services && <th className="event__col-services">{t('common.services')}</th>}
                  {visibleColumns.date && (
                    <th
                      className="event__col-date event__sortable-header"
                      onClick={() => handleSort('date')}
                    >
                      {t('common.date')} {' '}
                      {sortBy === 'date' ? (
                        sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
                      ) : (
                        <FaSort className="event__sort-icon--muted" />
                      )}
                    </th>
                  )}
                  {visibleColumns.team && <th className="event__col-team">{t('event.colTeam')}</th>}
                  {(visibleColumns.invitedBy ?? visibleColumns.referredBy) && <th className="event__col-referred-by">{t('event.colInvitedBy') || t('event.colReferredBy')}</th>}
                  {visibleColumns.location && <th className="event__col-location">{t('common.location')}</th>}
                  {visibleColumns.status && <th className="event__col-status">{t('common.status')}</th>}
                  <th className="event__col-actions">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvents.map((event, index) => (
                  <tr
                    key={event.id}
                    className={isEventWithoutDate(event) ? 'event__row--no-date' : ''}
                    tabIndex={0}
                    onClick={() => handleViewEvent(event)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleViewEvent(event);
                      }
                    }}
                    role="button"
                    aria-pressed="false"
                  >
                    <td className="event__col-no">
                      <span className="event__id">{getSerialNumber(event.id)}</span>
                    </td>
                    {visibleColumns.id && (
                      <td className="event__col-id">
                        <span className="event__event-id"><Highlight text={getEventIdentifier(event)} query={searchQuery} /></span>
                      </td>
                    )}
                    {visibleColumns.orderType && (
                      <td className="event__col-order-type">
                        {(() => {
                          const ot = (event.orderType || 'own').toString().toLowerCase();
                          return (
                            <span className={`event__order-type event__order-type--${ot}`}>
                              {formatOrderType(event.orderType || 'own')}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    <td className="event__col-client">
                      <div className="event__client">
                        <div className="event__client-info">
                          <div className="event__client-name"><Highlight text={event.clientName} query={searchQuery} /></div>
                          <div className="event__client-phone"><Highlight text={event.clientPhone} query={searchQuery} /></div>
                          <div className="event__client-email"><Highlight text={event.clientEmail} query={searchQuery} /></div>
                        </div>
                      </div>
                    </td>
                    {visibleColumns.quotationNo && (
                      <td className="event__col-quotation-no">
                        <span className="event__quotation-id">
                          <Highlight text={event.quotationRef || 'NA'} query={searchQuery} />
                        </span>
                      </td>
                    )}
                    {visibleColumns.services && (
                      <td className="event__col-services">
                        <div className="event__services">
                          {(() => {
                            // Sort services by date (chronological order)
                            const sortedServices = [...event.services].sort((a, b) => {
                              const dateA = a.date ? new Date(a.date) : new Date(0);
                              const dateB = b.date ? new Date(b.date) : new Date(0);
                              return dateA - dateB;
                            });
                            // show up to 4 services, then a +N indicator
                            const visibleSvcs = sortedServices.slice(0, 4);
                            return (
                              <>
                                {visibleSvcs.map((service, idx) => {
                                  const isStruck = service && (service.status === 'completed' || service.status === 'cancelled');
                                  return (
                                    <span key={idx} className={`event__service-tag ${isStruck ? 'event__service-tag--struck' : ''}`}>
                                      <FaConciergeBell /> {service.name}
                                    </span>
                                  );
                                })}
                                {sortedServices.length > visibleSvcs.length && (
                                  <span className="event__service-more">+{sortedServices.length - visibleSvcs.length}</span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    )}
                    
                    {visibleColumns.date && (
                      <td className="event__col-date">
                        <div className="event__date-info">
                          {event.services.length > 0 ? (
                            (() => {
                              // Sort services by date (chronological order)
                              const sortedServices = [...event.services].sort((a, b) => {
                                const dateA = a.date ? new Date(a.date) : new Date(0);
                                const dateB = b.date ? new Date(b.date) : new Date(0);
                                return dateA - dateB;
                              });
                              const visibleDates = sortedServices.slice(0, 4);
                              return (
                                <>
                                  {visibleDates.map((service, idx) => {
                                    const isStruck = service && (service.status === 'completed' || service.status === 'cancelled');
                                    return (
                                      <div key={idx} className="event__service-date-time">
                                        <span className={`event__date ${isStruck ? 'event__date--struck' : ''}`}>{formatDateDisplay(service.date)}</span>
                                        {service.time && (
                                          <span className={`event__time ${isStruck ? 'event__time--struck' : ''}`}>
                                            <FaClock /> {service.time}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {sortedServices.length > visibleDates.length && (
                                    <div className="event__service-more">+{sortedServices.length - visibleDates.length}</div>
                                  )}
                                </>
                              );
                            })()
                          ) : (
                            <span className="event__date">N/A</span>
                          )}
                        </div>
                      </td>
                    )}
                    {visibleColumns.team && (
                      <td className="event__col-team">
                        <div className="event__services">
                          {event.team && event.team.map((member, mIdx) => {
                            const { initials, hue } = getMemberChipIdentity(member);
                            const chipFm = fullTeamMembers.find(x => String(x?.name || '').trim().toLowerCase() === String(member).trim().toLowerCase());
                            const chipAvatar = resolveMemberAvatar(chipFm);
                            return (
                              <button
                                key={mIdx}
                                type="button"
                                className="event__team-chip"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTeamPaymentModal(member, event);
                                }}
                                title={`Open payment for ${member}`}
                              >
                                <span
                                  className="event__team-chip-avatar"
                                  aria-hidden="true"
                                  style={{ background: `linear-gradient(135deg, hsl(${hue}, 68%, 55%), hsl(${(hue + 45) % 360}, 68%, 42%))` }}
                                >
                                  <span className="event__team-chip-avatar-initials">{initials}</span>
                                  {chipAvatar && (
                                    <img
                                      src={chipAvatar}
                                      alt=""
                                      loading="lazy"
                                      className="event__team-chip-avatar-img"
                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                  )}
                                </span>
                                <span className="event__team-member-name">
                                  <Highlight text={member} query={searchQuery} />
                                </span>
                                <FaArrowRight className="event__team-payment-icon" />
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    )}
                    {(visibleColumns.invitedBy ?? visibleColumns.referredBy) && (
                      <td className="event__col-referred-by">
                        {String(event.orderType || 'own').toLowerCase() === 'suborder' ? (
                          <span className="event__referred-by"><Highlight text={getInvitedBy(event) || '—'} query={searchQuery} /></span>
                        ) : (
                          <span className="event__referred-by event__referred-by--na">NA</span>
                        )}
                      </td>
                    )}
                    {visibleColumns.location && (
                      <td className="event__col-location">
                        <div className="event__location">
                          {event.services.length > 0 ? (
                            (() => {
                              // Sort services by date (chronological order)
                              const sortedServices = [...event.services].sort((a, b) => {
                                const dateA = a.date ? new Date(a.date) : new Date(0);
                                const dateB = b.date ? new Date(b.date) : new Date(0);
                                return dateA - dateB;
                              });
                              const visibleLocations = sortedServices.slice(0, 4);
                              return (
                                <>
                                  {visibleLocations.map((service, idx) => {
                                    const isStruck = service && (service.status === 'completed' || service.status === 'cancelled');
                                    return (
                                      <div key={idx} className="event__service-location-item">
                                        <FaMapMarkerAlt />
                                        {(() => {
                                          const locText = getServiceShortLocationText(service) || service.location || '';
                                          const mapUrl = getServiceMapUrl(service);
                                          const linkOn = locText && mapUrl && isServiceMapLinkEnabled(service);
                                          return linkOn ? (
                                            <a
                                              className={`event__location-link${isStruck ? ' event__location--struck' : ''}`}
                                              href={mapUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              title="Open in Google Maps"
                                              data-tooltip-pos="top"
                                            >
                                              <Highlight text={locText} query={searchQuery} />
                                              <FaLocationArrow className="event__location-link-icon" aria-hidden="true" />
                                            </a>
                                          ) : (
                                            <span className={`event__location-name ${isStruck ? 'event__location--struck' : ''}`}>
                                              <Highlight text={locText || 'N/A'} query={searchQuery} />
                                            </span>
                                          );
                                        })()}
                                      </div>
                                    );
                                  })}
                                  {sortedServices.length > visibleLocations.length && (
                                    <div className="event__service-more">+{sortedServices.length - visibleLocations.length}</div>
                                  )}
                                </>
                              );
                            })()
                          ) : (
                            <div className="event__location-item">
                              <FaMapMarkerAlt />
                              <span>N/A</span>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="event__col-status">
                        <span 
                          className={`event__status event__status--${event.status}`}
                        >
                          {getStatusIcon(event.status)}
                          {event.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </span>
                      </td>
                    )}
                    <td className="event__col-actions">
                      <div className="event__actions">
                        <button
                          className="event__action-btn event__action-btn--view"
                          onClick={(e) => { e.stopPropagation(); handleViewEvent(event); }}
                          title="View"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="event__action-btn event__action-btn--whatsapp"
                          onClick={(e) => { e.stopPropagation(); handleWhatsApp(event); }}
                          title="WhatsApp"
                        >
                          <FaWhatsapp />
                        </button>
                        <button
                          className="event__action-btn event__action-btn--email"
                          onClick={(e) => { e.stopPropagation(); handleEmail(event); }}
                          title="Email"
                        >
                          <FaEnvelope />
                        </button>
                        <button
                          className="event__action-btn event__action-btn--delete"
                          onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="event__grid">
            {paginatedEvents.map((event, index) => (
              <div key={event.id} className={`event__card${isEventWithoutDate(event) ? ' event__card--no-date' : ''}`}>
                <div className="event__card-header">
                  <div className="event__card-header-row event__card-header-row--top">
                    <span className="event__card-no">#{getSerialNumber(event.id)}</span>
                    <span className="event__card-id">
                      <Highlight text={getEventIdentifier(event)} query={searchQuery} />
                    </span>
                  </div>
                  <div className="event__card-header-row event__card-header-row--bottom">
                    {(() => {
                      const ot = (event.orderType || 'own').toString().toLowerCase() === 'suborder' ? 'suborder' : 'own';
                      return (
                        <span className={`event__order-type event__order-type--${ot}`}>
                          {formatOrderType(event.orderType || 'own')}
                        </span>
                      );
                    })()}
                    <span className="event__card-status">
                      <span className={`event__status event__status--${event.status}`}>
                        {getStatusIcon(event.status)}
                        {event.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </span>
                    </span>
                  </div>
                </div>
                
                <div className="event__card-client">
                  <div className="event__card-avatar">
                    {event.clientName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="event__card-client-info">
                    <h3>{event.clientName}</h3>
                    <p>{event.clientPhone}</p>
                    <p>{event.clientEmail}</p>
                    {String(event.orderType || 'own').toLowerCase() === 'suborder' && (
                      <p className="event__card-referred-by">
                        {t('event.colInvitedBy') || t('event.colReferredBy')}: {(getInvitedBy(event) || '—')}
                      </p>
                    )}
                    {event.team && (
                      <div className="event__team-list">
                        {event.team.map((m, i) => {
                          const { initials, hue } = getMemberChipIdentity(m);
                          const cardFm = fullTeamMembers.find(x => String(x?.name || '').trim().toLowerCase() === String(m).trim().toLowerCase());
                          const cardAvatar = resolveMemberAvatar(cardFm);
                          return (
                            <button
                              key={i}
                              type="button"
                              className="event__team-chip"
                              onClick={(e) => {
                                e.stopPropagation();
                                openTeamPaymentModal(m, event);
                              }}
                              title={`Open payment for ${m}`}
                            >
                              <span
                                className="event__team-chip-avatar"
                                aria-hidden="true"
                                style={{ background: `linear-gradient(135deg, hsl(${hue}, 68%, 55%), hsl(${(hue + 45) % 360}, 68%, 42%))` }}
                              >
                                <span className="event__team-chip-avatar-initials">{initials}</span>
                                {cardAvatar && (
                                  <img
                                    src={cardAvatar}
                                    alt=""
                                    loading="lazy"
                                    className="event__team-chip-avatar-img"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  />
                                )}
                              </span>
                              <span className="event__team-member-name">{m}</span>
                              <FaArrowRight className="event__team-payment-icon" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="event__card-services">
                  <h4><FaConciergeBell /> Services ({event.services.length})</h4>
                  {(() => {
                    // Scheduled services first, then completed, then cancelled; date order within each group
                    const statusRank = (s) => {
                      const st = (s?.status || 'scheduled').toLowerCase();
                      if (st === 'completed') return 1;
                      if (st === 'cancelled') return 2;
                      return 0;
                    };
                    const sortedServices = [...event.services].sort((a, b) => {
                      const rankDiff = statusRank(a) - statusRank(b);
                      if (rankDiff !== 0) return rankDiff;
                      const dateA = a.date ? new Date(a.date) : new Date(0);
                      const dateB = b.date ? new Date(b.date) : new Date(0);
                      return dateA - dateB;
                    });
                    const visible = sortedServices.slice(0, 1);
                    return (
                      <>
                        {visible.map((service, idx) => {
                          const dateText = formatDateDisplay(service.date);
                          const timeText = service.time ? String(service.time).trim() : '';
                          const locText = String(getServiceShortLocationText(service) || service.location || '').trim();
                          const hasDate = Boolean(dateText);
                          const hasTime = Boolean(timeText);
                          const hasLocation = Boolean(locText);
                          return (
                          <div key={idx} className="event__card-service">
                            <div className="event__card-service-header">
                              <span className="event__service-name"><Highlight text={service.name} query={searchQuery} /></span>
                              <span 
                                className={`event__service-status event__service-status--${service.status}`}
                              >
                                {getStatusIcon(service.status)}
                              </span>
                            </div>
                            {(hasDate || hasTime) && (
                            <div className="event__card-service-details">
                              {hasDate && (<span><FaCalendarAlt /> {dateText}</span>)}
                              {hasTime && (<span><FaClock /> {timeText}</span>)}
                            </div>
                            )}
                            {hasLocation && (
                            <div className="event__card-service-location">
                              <FaMapMarkerAlt />
                              {(() => {
                                const mapUrl = getServiceMapUrl(service);
                                const linkOn = locText && mapUrl && isServiceMapLinkEnabled(service);
                                return linkOn ? (
                                  <a
                                    className="event__location-link"
                                    href={mapUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Open location in Google Maps"
                                    data-tooltip-pos="top"
                                  >
                                    <Highlight text={locText} query={searchQuery} />
                                    <FaLocationArrow className="event__location-link-icon" aria-hidden="true" />
                                  </a>
                                ) : (
                                  <span><Highlight text={locText} query={searchQuery} /></span>
                                );
                              })()}
                            </div>
                            )}
                          </div>
                          );
                        })}
                        {sortedServices.length > visible.length && (
                          <div className="event__service-more">+{sortedServices.length - visible.length}</div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="event__card-actions">
                  <button
                    className="event__action-btn event__action-btn--view"
                    onClick={() => handleViewEvent(event)}
                    title="View"
                  >
                    <FaEye />
                  </button>
                  <button
                    className="event__action-btn event__action-btn--whatsapp"
                    onClick={() => handleWhatsApp(event)}
                    title="Send WhatsApp"
                  >
                    <FaWhatsapp />
                  </button>
                  <button
                    className="event__action-btn event__action-btn--email"
                    onClick={() => handleEmail(event)}
                    title="Send Email"
                  >
                    <FaEnvelope />
                  </button>
                  <button
                    className="event__action-btn event__action-btn--delete"
                    onClick={() => handleDeleteEvent(event.id)}
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredEvents.length > 0 && (
        <div className="event__pagination">
          <div className="event__pagination-info">
            {t('common.showing')} {startIndex + 1} {t('common.to')} {Math.min(endIndex, filteredEvents.length)} {t('common.of')} {filteredEvents.length} {t('event.eventsLabel')}
          </div>
          <div className="event__pagination-controls">
            <div className="event__pagination-rows">
              <label>{t('common.rowsPerPage')}</label>
              <CustomSelect
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                options={[
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                ]}
              />
            </div>
            <div className="event__pagination-buttons">
              <button
                className="event__pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First Page"
              >
                <FaAngleDoubleLeft />
              </button>
              <button
                className="event__pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous Page"
              >
                <FaAngleLeft />
              </button>
              <div className="event__pagination-pages">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                  
                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }

                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        className="event__pagination-btn"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1" className="event__pagination-ellipsis">...</span>);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`event__pagination-btn ${currentPage === i ? 'active' : ''}`}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<span key="ellipsis2" className="event__pagination-ellipsis">...</span>);
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        className="event__pagination-btn"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>
              <button
                className="event__pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next Page"
              >
                <FaAngleRight />
              </button>
              <button
                className="event__pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last Page"
              >
                <FaAngleDoubleRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      <Modal
        isOpen={showCreateModal}
        contentClassName="event__modal"
        title={t('event.createTitle')}
        headerActions={(
          <>
            <button
              type="button"
              className={`modal__toggle-btn ${createEventReminderEnabled ? 'modal__toggle-btn--active' : 'modal__toggle-btn--inactive'}`}
              onClick={() => setCreateEventReminderEnabled((prev) => !prev)}
              aria-pressed={createEventReminderEnabled}
              aria-label={`Event reminder ${createEventReminderEnabled ? 'on' : 'off'}`}
              title={`Event reminder ${createEventReminderEnabled ? 'on' : 'off'}`}
            >
              {createEventReminderEnabled ? <FaBell className="modal__toggle-icon" /> : <FaClock className="modal__toggle-icon" />}
              <span className="modal__toggle-state">{createEventReminderEnabled ? t('common.on') : t('common.off')}</span>
            </button>
            <button className="btn-secondary modal__reset" onClick={resetCreateForm} type="button">
              {t('common.reset')}
            </button>
          </>
        )}
        onClose={() => { saveDraftToSession(DRAFT_KEY); setShowCreateModal(false); setCreateEventReminderEnabled(false); }}
        size="large"
      >
        <div className="event__form">
          <div className="event__form-section">
            <div className="event__form-row">
              <div className="event__form-group">
                <label>{t('event.labelEventId')}</label>
                <input
                  type="text"
                  value={formData.quotationId || ''}
                  readOnly
                />
              </div>
              <div className="event__form-group">
                <label>{t('event.labelQuotationId')}</label>
                {Array.isArray(quotations) && quotations.length > 0 ? (
                  (() => {
                    const all = quotations || [];
                    const filtered = all.filter(q => {
                      const label = String(q.id || q.quotationId || q.ref) + (q.clientName ? ' - ' + q.clientName : '');
                      return label.toLowerCase().includes((quotationQuery || '').toLowerCase());
                    });

                    const selectedLabel = (() => {
                      if (quotationQuery) return quotationQuery;
                      if (formData.quotationRef) {
                        const found = all.find(x => String(x.id || x.quotationId || x.ref) === String(formData.quotationRef));
                        if (found) return String(found.id || found.quotationId || found.ref) + (found.clientName ? ' - ' + found.clientName : '');
                      }
                      return '';
                    })();

                    return (
                      <div style={{ position: 'relative' }}>
                        <input
                          ref={quotationInputRef}
                          className="event__quotation-input"
                          type="text"
                          placeholder={t('event.searchQuotation')}
                          value={quotationQuery || selectedLabel}
                          onChange={(e) => { setQuotationQuery(e.target.value); setShowQuotationDropdown(true); setHighlightedQuotationIndex(-1); }}
                          onFocus={() => { setShowQuotationDropdown(true); setHighlightedQuotationIndex(-1); }}
                          onKeyDown={(e) => {
                            const max = filtered.length - 1;
                            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedQuotationIndex(i => Math.min(max, Math.max(0, i + 1))); }
                            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedQuotationIndex(i => Math.max(0, i - 1)); }
                            else if (e.key === 'Enter') {
                              e.preventDefault();
                              if (highlightedQuotationIndex >= 0 && filtered[highlightedQuotationIndex]) {
                                const sel = filtered[highlightedQuotationIndex];
                                const selId = String(sel.id || sel.quotationId || sel.ref);
                                handleSelectQuotation(selId);
                                setQuotationQuery(String(selId) + (sel.clientName ? ' - ' + sel.clientName : ''));
                                setShowQuotationDropdown(false);
                                setHighlightedQuotationIndex(-1);
                              }
                            } else if (e.key === 'Escape') {
                              setShowQuotationDropdown(false);
                            }
                          }}
                          onBlur={() => { quotationBlurTimeoutRef.current = setTimeout(() => setShowQuotationDropdown(false), 120); }}
                          onMouseDown={() => { if (quotationBlurTimeoutRef.current) { clearTimeout(quotationBlurTimeoutRef.current); quotationBlurTimeoutRef.current = null; } }}
                        />

                        {showQuotationDropdown && filtered.length > 0 && (
                          <ul className="event__quotation-dropdown" style={{ position: 'absolute', zIndex: 40, top: 'calc(100% + 6px)', left: 0, right: 0, maxHeight: 220, overflow: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 0, margin: 0, listStyle: 'none' }}>
                            {filtered.map((q, idx) => {
                              const label = String(q.id || q.quotationId || q.ref) + (q.clientName ? ' - ' + q.clientName : '');
                              const isHighlighted = idx === highlightedQuotationIndex;
                              return (
                                <li
                                  key={(q.id || q.quotationId || q.ref) + '-' + idx}
                                  onMouseDown={(ev) => { ev.preventDefault(); const selId = String(q.id || q.quotationId || q.ref); handleSelectQuotation(selId); setQuotationQuery(label); setShowQuotationDropdown(false); setHighlightedQuotationIndex(-1); }}
                                  onMouseEnter={() => setHighlightedQuotationIndex(idx)}
                                  style={{ padding: '8px 10px', background: isHighlighted ? '#f3f4f6' : '#fff', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                                >
                                  <div style={{ fontSize: 13, color: '#111' }}>{label}</div>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {(quotationQuery || formData.quotationRef) && (
                          <button
                            type="button"
                            className="event__quotation-clear"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setQuotationQuery('');
                              resetCreateForm();
                              setShowQuotationDropdown(false);
                              setHighlightedQuotationIndex(-1);
                              try { quotationInputRef.current && quotationInputRef.current.focus(); } catch (err) {}
                            }}
                            aria-label="Clear quotation"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <div className="event__form-note">
                    {t('event.noQuotations')}
                  </div>
                )}
              </div>
              <div className="event__form-group">
                <label>{t('common.orderType')}</label>
                <div className="event__custom-select" ref={orderTypeDropdownRef}>
                  <button
                    type="button"
                    className={`event__custom-select-trigger${orderTypeDropdownOpen ? ' event__custom-select-trigger--open' : ''}`}
                    onClick={() => setOrderTypeDropdownOpen(prev => !prev)}
                  >
                    <span className={`event__custom-select-value event__custom-select-value--${formData.orderType}`}>
                      {formData.orderType === 'suborder' ? t('common.suborder') : t('common.own')}
                    </span>
                    <FaChevronDown className={`event__custom-select-chevron${orderTypeDropdownOpen ? ' event__custom-select-chevron--open' : ''}`} />
                  </button>
                  {orderTypeDropdownOpen && (
                    <ul className="event__custom-select-dropdown">
                      <li
                        className={`event__custom-select-option${formData.orderType === 'own' ? ' event__custom-select-option--active' : ''}`}
                        onClick={() => { setFormData({ ...formData, orderType: 'own', invitedBy: '', referredBy: '' }); setOrderTypeDropdownOpen(false); }}
                      >
                        {t('common.own')}
                      </li>
                      <li
                        className={`event__custom-select-option${formData.orderType === 'suborder' ? ' event__custom-select-option--active' : ''}`}
                        onClick={() => { setFormData({ ...formData, orderType: 'suborder' }); setOrderTypeDropdownOpen(false); }}
                      >
                        {t('common.suborder')}
                      </li>
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="event__form-row event__form-row--with-margin">
              <div className="event__form-group">
                <label>{t('event.labelClientName')}</label>
                <input
                  type="text"
                  placeholder={t('event.enterClientName')}
                  value={formData.clientName}
                  onChange={(e) => {
                    // allow letters, spaces and common name punctuation only
                    const v = (e.target.value || '').replace(/[^A-Za-z '\-\.()]/g, '');
                    setFormData({ ...formData, clientName: v });
                  }}
                  inputMode="text"
                  title="Name: letters, spaces and - ' . () only"
                />
              </div>
              <div className="event__form-group">
                <label>Client Phone *</label>
                <input
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={formData.clientPhone}
                  onChange={(e) => setFormData({ ...formData, clientPhone: sanitizePhone(e.target.value) })}
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  title="Phone: 10 digits, numbers only"
                />
              </div>
              <div className="event__form-group">
                <label>{t('event.labelClientEmail')}</label>
                <input
                  type="email"
                  placeholder="client@email.com"
                  value={formData.clientEmail}
                  onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                  inputMode="email"
                  title="Email address"
                />
              </div>
            </div>
          </div>

          <div className="event__form-section">
            <div className="event__form-section-header">
              <h3>{t('event.sectionServices')}</h3>
              <button className="btn-secondary" onClick={addService}>
                <FaPlus /> {t('event.addService')}
              </button>
            </div>

            {formData.services.map((service, index) => (
              <div key={index} className="event__service-item">
                <div className="event__service-item-header">
                  <h4>{t('quotation.colService')} {index + 1}</h4>
                  {formData.services.length > 1 && (
                    <button
                      className="event__remove-service"
                      onClick={() => removeService(index)}
                      title="Remove service"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                <div className="event__form-group">
                  <label>{t('event.labelServiceType')}</label>
                  <ServiceTypeInput
                    value={service.name || ''}
                    onChange={(val) => updateService(index, 'name', val)}
                    availableServices={allServices}
                    onNewService={handleNewService}
                  />
                </div>

                <div className="event__form-row">
                  <div className="event__form-group">
                    <label>{t('common.date')}</label>
                    <div className="event__form-input-with-flag">
                      <input
                        type="date"
                        value={service.date}
                        onChange={(e) => updateService(index, 'date', e.target.value)}
                      />
                      {index > 0 && (
                        <label className="event__same-as-above">
                          <input
                            type="checkbox"
                            checked={!!service.sameAsAbove}
                            onChange={(e) => toggleSameAsAbove(index, e.target.checked)}
                          />
                          <span>{t('event.sameAsAbove')}</span>
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="event__form-group">
                    <label>{t('event.labelTime')}</label>
                    <div className="event__form-input-with-flag">
                      <input
                        type="time"
                        value={service.time}
                        onChange={(e) => updateService(index, 'time', e.target.value)}
                      />
                      {index > 0 && (
                        <label className="event__same-as-above" style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                          {/* Hidden placeholder so layout stays consistent when unified checkbox is used */}
                          <input type="checkbox" disabled />
                          <span>Same as above</span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                <div className="event__form-group">
                  <div className="event__label-row">
                    <label>{t('common.location')}</label>
                    <label className="event__map-toggle" title="Show location as a map link">
                      <span className="event__map-toggle-text">Map</span>
                      <span className="switch switch--small">
                        <input
                          type="checkbox"
                          checked={service.locationMapEnabled !== false}
                          onChange={(e) => updateService(index, 'locationMapEnabled', e.target.checked)}
                          aria-label="Link location to map"
                        />
                        <span className="slider" />
                      </span>
                    </label>
                  </div>
                  <div className="event__form-input-with-flag">
                    <LocationInput
                      value={service.location || ''}
                      detail={service.locationDetail || null}
                      placeholder={t('event.enterLocation')}
                      onChange={(loc, detail) => updateServiceLocation(index, loc, detail)}
                    />
                    {index > 0 && (
                      <label className="event__same-as-above" style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                        {/* Hidden placeholder so layout stays consistent when unified checkbox is used */}
                        <input type="checkbox" disabled />
                        <span>Same as above</span>
                      </label>
                    )}
                  </div>
                </div>

                <div className="event__form-group">
                  <label>{t('common.notes')}</label>
                  <textarea
                    placeholder={t('event.notesPlaceholder')}
                    value={service.notes}
                    onChange={(e) => updateService(index, 'notes', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="event__form-section">
            <div className="event__form-section-header event__team-head">
              <h3>{t('event.sectionTeam')}</h3>
              <div className="event__team-head-actions">
                <label className="event__notify-toggle" title={t('event.notifyAssigneesHint')}>
                  <span className="event__notify-toggle-label">{t('event.notifyAssignees')}</span>
                  <span className={`event__switch${formData.notifyAssignees !== false ? ' event__switch--on' : ''}`} onClick={() => setFormData({ ...formData, notifyAssignees: !(formData.notifyAssignees !== false) })} role="switch" aria-checked={formData.notifyAssignees !== false} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFormData({ ...formData, notifyAssignees: !(formData.notifyAssignees !== false) }); } }}>
                    <span className="event__switch-thumb" />
                  </span>
                </label>
                <button type="button" className="btn-secondary event__team-add-btn" onClick={() => { setQuickTeamErrors({}); setShowTeamQuickAdd(true); }} title="Add team member"><FaPlus /> Add</button>
              </div>
            </div>
              <div className="event__form-group">
              {/* <label>Team Members</label> */}
              <div className="event__team-list event__team-list--selectable">
                {teamMembers.length === 0 ? (
                  <div className="event__muted">{t('event.noTeamMembers')}</div>
                ) : (
                  teamMembers.map((member) => {
                    const checked = Array.isArray(formData.team) && formData.team.includes(member);
                    return (
                      <label key={member} className={`event__team-label ${checked ? 'is-checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const nextTeam = Array.isArray(formData.team) ? [...formData.team] : [];
                            if (e.target.checked) {
                              nextTeam.push(member);
                            } else {
                              const idx = nextTeam.indexOf(member);
                              if (idx > -1) nextTeam.splice(idx, 1);
                            }
                            setFormData({ ...formData, team: nextTeam });
                          }}
                        />
                        <span className="event__team-label-text">{member}{(() => { const fm = fullTeamMembers.find(x => String(x?.name||'').trim().toLowerCase() === String(member).trim().toLowerCase()); const role = fm?.role ? String(fm.role).trim() : ''; return role ? ` — ${role}` : ''; })()}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {formData.orderType === 'suborder' && (
            <div className="event__form-section event__form-section--invited-by">
              <div className="event__form-section-header">
                <h3>{t('event.sectionInvitedBy') || t('event.labelInvitedBy') || t('event.labelReferredBy')}</h3>
              </div>
              <div className="event__form-group">
                <input
                  type="text"
                  placeholder={t('event.invitedByPlaceholder') || t('event.referredByPlaceholder')}
                  value={formData.invitedBy || formData.referredBy || ''}
                  onChange={(e) => setFormData({ ...formData, invitedBy: e.target.value, referredBy: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="event__form-actions">
            <button
              className="btn-secondary"
              onClick={() => {
                try { saveDraftToSession(makeDraftKey()); } catch (e) {}
                  try { setShowCreateModal(false); } catch (e) {}
                  try { showToast('⚠️ your form saved temporarily, please complete and submit soon.'); } catch (e) {}
              }}
              type="button"
            >
              {t('common.saveDraft')}
            </button>
            <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
              {t('common.cancel')}
            </button>
            <button 
              className="btn-primary" 
              onClick={() => {
                // validate before submit
                if (!isValidName(formData.clientName)) {
                  showToast('Please enter a valid client name');
                  return;
                }
                if (!isValidPhone(formData.clientPhone)) {
                  showToast('Please enter a valid 10-digit phone number (numbers only)');
                  return;
                }
                if (!isValidEmail(formData.clientEmail)) {
                  showToast('Please enter a valid email address');
                  return;
                }
                handleSubmitEvent();
              }}
              disabled={!formData.clientName || !formData.clientPhone}
            >
              {t('event.createEvent')}
            </button>
          </div>
        </div>
      </Modal>

      {/* View/Edit Event Modal */}
      {showViewModal && selectedEvent && (
      <Modal
        isOpen={true}
        contentClassName="event__modal"
        title={isEditMode ? 'Edit Event' : (selectedEvent.id || '')}
        headerActions={(
          <>
            {/* Per-event calendar sync toggle removed */}
            <button
              type="button"
              className={`modal__toggle-btn ${isEventReminderEnabled ? 'modal__toggle-btn--active' : 'modal__toggle-btn--inactive'}`}
              onClick={handleToggleEventReminder}
              aria-pressed={isEventReminderEnabled}
              aria-label={`Event reminder ${isEventReminderEnabled ? 'on' : 'off'}`}
              title={`Event reminder ${isEventReminderEnabled ? 'on' : 'off'}`}
            >
              {isEventReminderEnabled ? <FaBell className="modal__toggle-icon" /> : <FaClock className="modal__toggle-icon" />}
              <span className="modal__toggle-state">{isEventReminderEnabled ? 'On' : 'Off'}</span>
            </button>
            {isEditMode ? (
              <div className="event__header-status-edit">
                <label htmlFor="event-status-select" className="sr-only">Event Status</label>
                {(() => {
                  const currentHeaderStatus = (formData.eventStatus || deriveEventStatusFromServices(formData.services || []) || selectedEvent.status).toString().toLowerCase();
                  return (
                    <CustomSelect
                      value={currentHeaderStatus}
                      onChange={(val) => setFormData({ ...formData, eventStatus: val })}
                      className={`event__header-status event__header-status--${currentHeaderStatus}`}
                      options={[
                        { value: 'scheduled', label: '● Scheduled' },
                        { value: 'completed', label: '● Completed' },
                        { value: 'cancelled', label: '● Cancelled' }
                      ]}
                    />
                  );
                })()}
              </div>
            ) : (
              <button className="btn-secondary" onClick={handleEnableEdit}><FaEdit /> Edit</button>
            )}
          </>
        )}
        onClose={() => {
          setShowViewModal(false);
          setSelectedEvent(null);
          setIsEditMode(false);
        }}
        size="large"
      >
        {!isEditMode ? (
          // View Mode
          <div className="event__view">
            {(() => {
              const clientName = String(selectedEvent.clientName || '—').trim() || '—';
              const { initials: clientInitials, hue: clientHue } = getMemberChipIdentity(clientName);
              return (
            <div className="event__view-header event__view-hero">
              <div className="event__view-hero-glow" aria-hidden="true" />
              <div className="event__view-hero-main">
                <div className="event__view-client-info">
                  <div className="event__view-client-row">
                    <h3><Highlight text={selectedEvent.clientName} query={searchQuery} /></h3>
                    <span
                      className={`event__status event__status--${selectedEvent.status}`}
                    >
                      {getStatusIcon(selectedEvent.status)}
                      {selectedEvent.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </div>
                  <div className="event__view-contact-chips">
                    {selectedEvent.clientPhone && (
                      <span className="event__chip">
                        <FaPhoneAlt className="event__chip-icon" aria-hidden="true" />
                        <Highlight text={selectedEvent.clientPhone} query={searchQuery} />
                      </span>
                    )}
                    {selectedEvent.clientEmail && (
                      <span className="event__chip">
                        <FaEnvelope className="event__chip-icon" aria-hidden="true" />
                        <Highlight text={selectedEvent.clientEmail} query={searchQuery} />
                      </span>
                    )}
                    <span className={`event__chip event__chip--quote${selectedEvent.quotationRef ? '' : ' event__chip--muted'}`}>
                      <FaHashtag className="event__chip-icon" aria-hidden="true" />
                      {selectedEvent.quotationRef
                        ? <><span className="event__chip-label">{t('event.viewQuotation')}</span> <Highlight text={selectedEvent.quotationRef} query={searchQuery} /></>
                        : <><span className="event__chip-label">{t('event.viewQuotation')}</span> {t('event.labelNA')}</>}
                    </span>
                  </div>
                </div>
              </div>
            </div>
              );
            })()}

            <div className="event__view-section event__view-section--services">
              <div className="event__view-section-title">
                <h4><FaConciergeBell /> {t('common.services')}</h4>
                <span className="event__count-pill">{selectedEvent.services.length}</span>
              </div>
              <div className="event__view-services event__timeline">
                {(() => {
                  // Sort services by date (chronological order)
                  const sortedServices = [...selectedEvent.services].sort((a, b) => {
                    const dateA = a.date ? new Date(a.date) : new Date(0);
                    const dateB = b.date ? new Date(b.date) : new Date(0);
                    return dateA - dateB;
                  });
                  return sortedServices.map((service, idx) => {
                    const isStruck = service && (service.status === 'completed' || service.status === 'cancelled');
                    const indexLabel = String(idx + 1).padStart(2, '0');
                    return (
                      <div key={idx} className={`event__view-service event__view-service--${service.status || 'scheduled'}`}>
                        <span className="event__view-service-rail" aria-hidden="true" />
                        <div className="event__view-service-header">
                          <span className="event__view-service-index" aria-hidden="true">{indexLabel}</span>
                          <h5 className={isStruck ? 'event__service-name--struck' : ''}>{service.name}</h5>
                          <div className="event__view-service-status">
                            <span className={`event__status event__status--${service.status}`}>
                              {getStatusIcon(service.status)}
                              {service.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </span>
                          </div>
                        </div>
                        {(() => {
                          const dateText = formatDateDisplay(service.date);
                          const timeText = service.time ? String(service.time).trim() : '';
                          const locText = String(getServiceShortLocationText(service) || service.location || '').trim();
                          const hasDate = Boolean(dateText);
                          const hasTime = Boolean(timeText);
                          const hasLocation = Boolean(locText);
                          if (!hasDate && !hasTime && !hasLocation) return null;
                          return (
                        <div className="event__view-service-details">
                          {hasDate && (
                          <div className="event__view-service-detail">
                            <span className="event__view-service-detail-icon is-date" aria-hidden="true">
                              <FaCalendarAlt />
                            </span>
                            <span className="event__view-service-detail-text">
                              <small>Date</small>
                              <span className={isStruck ? 'event__date--struck' : ''}>{dateText}</span>
                            </span>
                          </div>
                          )}
                          {hasTime && (
                          <div className="event__view-service-detail">
                            <span className="event__view-service-detail-icon is-time" aria-hidden="true">
                              <FaClock />
                            </span>
                            <span className="event__view-service-detail-text">
                              <small>Time</small>
                              <span className={isStruck ? 'event__time--struck' : ''}>{timeText}</span>
                            </span>
                          </div>
                          )}
                          {hasLocation && (
                          <div className="event__view-service-detail event__view-service-detail--venue">
                            <span className="event__view-service-detail-icon is-venue" aria-hidden="true">
                              <FaMapMarkerAlt />
                            </span>
                            <span className="event__view-service-detail-text">
                              <small>Venue</small>
                            {(() => {
                              const mapUrl = getServiceMapUrl(service);
                              const linkOn = locText && mapUrl && isServiceMapLinkEnabled(service);
                              return linkOn ? (
                                <a
                                  className={`event__location-link${isStruck ? ' event__location--struck' : ''}`}
                                  href={mapUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Open location in Google Maps"
                                  data-tooltip-pos="top"
                                >
                                  <span className="event__location-link-text">{locText}</span>
                                  <FaLocationArrow className="event__location-link-icon" aria-hidden="true" />
                                </a>
                              ) : (
                                <span className={isStruck ? 'event__location--struck' : ''}>{locText}</span>
                              );
                            })()}
                            </span>
                          </div>
                          )}
                        </div>
                          );
                        })()}
                        {service.notes && (
                          <div className="event__view-service-notes">
                            <FaStickyNote aria-hidden="true" />
                            <p><strong>{t('event.svcNotes')}</strong> {service.notes}</p>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {selectedEvent.team && selectedEvent.team.length > 0 && (
              <div className="event__view-section">
                <div className="event__view-section-header">
                  <h4>
                    <FaUser /> {t('event.teamMembersSection')}
                    <span className="event__team-count">{selectedEvent.team.length}</span>
                  </h4>
                  <button
                    type="button"
                    className="btn-secondary event__ping-btn"
                    onClick={() => handlePingAssignees()}
                    disabled={pingLoading}
                    title={t('event.pingTeamHint')}
                  >
                    <FaBell /> {pingLoading && !pingMember ? t('event.pinging') : t('event.pingTeam')}
                  </button>
                </div>
                <div className="event__team-grid">
                  {selectedEvent.team.map((member, idx) => {
                    const fm = fullTeamMembers.find(x => String(x?.name||'').trim().toLowerCase() === String(member).trim().toLowerCase());
                    const role = fm?.role ? String(fm.role).trim() : '';
                    const name = String(member || '').trim();
                    const { initials, hue } = getMemberChipIdentity(name);
                    const avatarSrc = resolveMemberAvatar(fm);
                    const isPinging = pingLoading && pingMember === name;
                    return (
                      <div
                        key={`${name}-${idx}`}
                        className={`event__member-card${isPinging ? ' is-pinging' : ''}`}
                        style={{ animationDelay: `${Math.min(idx, 8) * 45}ms` }}
                      >
                        <div className="event__member-top">
                          <span
                            className="event__member-avatar"
                            aria-hidden="true"
                            style={{ background: `linear-gradient(135deg, hsl(${hue}, 68%, 55%), hsl(${(hue + 45) % 360}, 68%, 42%))` }}
                          >
                            <span className="event__member-avatar-initials">{initials}</span>
                            {avatarSrc && (
                              <img
                                src={avatarSrc}
                                alt=""
                                loading="lazy"
                                className="event__member-avatar-img"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            )}
                          </span>
                          <span className="event__member-info">
                            <span className="event__member-name" title={name}>{name}</span>
                            {role
                              ? <span className="event__member-role-pill" title={role}>{role}</span>
                              : <span className="event__member-role-pill event__member-role-pill--none">Team member</span>}
                          </span>
                        </div>
                        <div className="event__member-actions">
                          <button
                            type="button"
                            className="event__member-action event__member-action--pay"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTeamPaymentModal(member, selectedEvent);
                            }}
                            title={`Open payment for ${member}`}
                            aria-label={`Open payment for ${member}`}
                          >
                            <FaMoneyBillWave />
                          </button>
                          <button
                            type="button"
                            className={`event__member-action event__member-action--ping${isPinging ? ' is-loading' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePingAssignees(member);
                            }}
                            disabled={pingLoading}
                            title={`${t('event.pingMemberHint')} ${member}`}
                            aria-label={`Send notification to ${member}`}
                          >
                            <FaBell />
                          </button>
                          <button
                            type="button"
                            className="event__member-action event__member-action--remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveTeamMemberFromView(member);
                            }}
                            title={`Remove ${member} from event`}
                            aria-label={`Remove ${member} from event`}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="event__view-section">
              <div className="event__view-info-grid event__view-info-grid--three">
                <div className="event__view-info-item">
                  <label>
                    {t('event.labelCreated')}
                    <span className="event__meta-badge">{formatDateTimeDisplay(selectedEvent.createdDate) || '—'}</span>
                  </label>
                </div>
                <div className="event__view-info-item">
                  <label>
                    {t('event.labelOrderTypeView')} 
                    <span className={`event__order-type event__order-type--${(selectedEvent.orderType || 'n/a').toString().toLowerCase()}`}>
                      {formatOrderType(selectedEvent.orderType || 'N/A')}
                    </span>
                  </label>
                </div>
                {String(selectedEvent.orderType || 'own').toLowerCase() === 'suborder' && (
                  <div className="event__view-info-item">
                    <label>
                      {t('event.colInvitedBy') || t('event.colReferredBy')}
                      <span className="event__meta-badge">{getInvitedBy(selectedEvent) || '—'}</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Edit Mode
          <div className="event__form">
            <div className="event__form-section">
              <div className="event__form-row">
                <div className="event__form-group">
                  <label>{t('event.labelEventId')}</label>
                  <input type="text" value={formData.quotationId || selectedEvent.id || ''} readOnly />
                </div>
                <div className="event__form-group">
                  <label>{t('event.labelQuotationId')}</label>
                  {Array.isArray(quotations) && quotations.length > 0 ? (
                    <select
                      value={formData.quotationRef || ''}
                      onChange={(e) => handleSelectQuotation(e.target.value)}
                    >
                      <option value="">-- Select existing quotation (optional) --</option>
                      {quotations.map(q => (
                        <option key={q.id} value={String(q.id)}>{q.id} - {q.clientName || q.clientEmail || ''}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="event__form-note">
                      No quotations available
                    </div>
                  )}
                </div>
                <div className="event__form-group">
                  <label>{t('common.orderType')}</label>
                  <div className="event__custom-select" ref={orderTypeDropdownRef}>
                    <button
                      type="button"
                      className={`event__custom-select-trigger${orderTypeDropdownOpen ? ' event__custom-select-trigger--open' : ''}`}
                      onClick={() => setOrderTypeDropdownOpen(prev => !prev)}
                    >
                      <span className={`event__custom-select-value event__custom-select-value--${formData.orderType}`}>
                        {formData.orderType === 'suborder' ? t('common.suborder') : t('common.own')}
                      </span>
                      <FaChevronDown className={`event__custom-select-chevron${orderTypeDropdownOpen ? ' event__custom-select-chevron--open' : ''}`} />
                    </button>
                    {orderTypeDropdownOpen && (
                      <ul className="event__custom-select-dropdown">
                        <li
                          className={`event__custom-select-option${formData.orderType === 'own' ? ' event__custom-select-option--active' : ''}`}
                          onClick={() => { setFormData({ ...formData, orderType: 'own', invitedBy: '', referredBy: '' }); setOrderTypeDropdownOpen(false); }}
                        >
                          {t('common.own')}
                        </li>
                        <li
                          className={`event__custom-select-option${formData.orderType === 'suborder' ? ' event__custom-select-option--active' : ''}`}
                          onClick={() => { setFormData({ ...formData, orderType: 'suborder' }); setOrderTypeDropdownOpen(false); }}
                        >
                          {t('common.suborder')}
                        </li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>
              <div className="event__form-row event__form-row--with-margin">
                <div className="event__form-group">
                  <label>{t('event.labelClientName')}</label>
                  <input
                    type="text"
                    placeholder="Enter client name"
                    value={formData.clientName}
                    onChange={(e) => {
                      const v = (e.target.value || '').replace(/[^A-Za-z '\-\.()]/g, '');
                      setFormData({ ...formData, clientName: v });
                    }}
                    inputMode="text"
                    title="Name: letters, spaces and - ' . () only"
                  />
                </div>
                <div className="event__form-group">
                  <label>{t('event.labelClientPhone')}</label>
                  <input
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={formData.clientPhone}
                    onChange={(e) => setFormData({ ...formData, clientPhone: sanitizePhone(e.target.value) })}
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    title="Phone: 10 digits, numbers only"
                  />
                </div>
                <div className="event__form-group">
                  <label>{t('event.labelClientEmail')}</label>
                  <input
                    type="email"
                    placeholder="client@email.com"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                    inputMode="email"
                    title="Email address"
                  />
                </div>
              </div>
            </div>

            <div className="event__form-section">
              <div className="event__form-section-header">
                <h3>{t('event.sectionServices')}</h3>
                <button className="btn-secondary" onClick={addService}>
                  <FaPlus /> {t('event.addService')}
                </button>
              </div>

              {formData.services.map((service, index) => (
                <div key={index} className="event__service-item">
                  <div className="event__service-item-header">
                    <h4>{t('quotation.colService')} {index + 1}</h4>
                    {formData.services.length > 1 && (
                      <button
                        className="event__remove-service"
                        onClick={() => removeService(index)}
                        title="Remove service"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>

                  <div className="event__form-group">
                    <label>{t('event.labelServiceType')}</label>
                    <ServiceTypeInput
                      value={service.name || ''}
                      onChange={(val) => updateService(index, 'name', val)}
                      availableServices={allServices}
                      onNewService={handleNewService}
                    />
                  </div>

                  <div className="event__form-row">
                    <div className="event__form-group">
                      <label>{t('common.date')}</label>
                      <div className="event__form-input-with-flag">
                        <input
                          type="date"
                          value={service.date}
                          onChange={(e) => updateService(index, 'date', e.target.value)}
                        />
                        {index > 0 && (
                          <label className="event__same-as-above">
                            <input
                              type="checkbox"
                              checked={!!service.sameAsAbove}
                              onChange={(e) => toggleSameAsAbove(index, e.target.checked)}
                            />
                            <span>{t('event.sameAsAbove')}</span>
                          </label>
                        )}
                      </div>
                    </div>
                    <div className="event__form-group">
                      <label>{t('event.labelTime')}</label>
                      <div className="event__form-input-with-flag">
                        <input
                          type="time"
                          value={service.time}
                          onChange={(e) => updateService(index, 'time', e.target.value)}
                        />
                        {index > 0 && (
                          <label className="event__same-as-above" style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                            <input type="checkbox" disabled />
                            <span>Same as above</span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="event__form-group">
                  <div className="event__label-row">
                    <label>{t('common.location')}</label>
                    <label className="event__map-toggle" title="Show location as a map link">
                      <span className="event__map-toggle-text">Map</span>
                      <span className="switch switch--small">
                        <input
                          type="checkbox"
                          checked={service.locationMapEnabled !== false}
                          onChange={(e) => updateService(index, 'locationMapEnabled', e.target.checked)}
                          aria-label="Link location to map"
                        />
                        <span className="slider" />
                      </span>
                    </label>
                  </div>
                    <div className="event__form-input-with-flag">
                      <LocationInput
                        value={service.location || ''}
                        detail={service.locationDetail || null}
                        placeholder="Enter event location"
                        onChange={(loc, detail) => updateServiceLocation(index, loc, detail)}
                      />
                        {index > 0 && (
                          <label className="event__same-as-above" style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                            <input type="checkbox" disabled />
                            <span>Same as above</span>
                          </label>
                        )}
                    </div>
                  </div>

                  <div className="event__form-row">
                    <div className="event__form-group">
                      <label>{t('common.status')}</label>
                      <CustomSelect
                        value={service.status || 'scheduled'}
                        onChange={(val) => updateService(index, 'status', val)}
                        className={`event__service-status-select event__service-status-select--${service.status || 'scheduled'}`}
                        options={[
                          { value: 'scheduled', label: `● ${t('event.statusScheduled')}` },
                          { value: 'completed', label: `● ${t('event.statusCompleted')}` },
                          { value: 'cancelled', label: `● ${t('event.statusCancelled')}` },
                        ]}
                      />
                    </div>
                  </div>

                  <div className="event__form-group">
                    <label>{t('common.notes')}</label>
                    <textarea
                      placeholder="Additional notes for this service"
                      value={service.notes}
                      onChange={(e) => updateService(index, 'notes', e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="event__form-section">
              <div className="event__form-section-header event__team-head">
                <h3>{t('event.sectionTeam')}</h3>
                <div className="event__team-head-actions">
                  <label className="event__notify-toggle" title={t('event.notifyAssigneesHint')}>
                    <span className="event__notify-toggle-label">{t('event.notifyAssignees')}</span>
                    <span className={`event__switch${formData.notifyAssignees !== false ? ' event__switch--on' : ''}`} onClick={() => setFormData({ ...formData, notifyAssignees: !(formData.notifyAssignees !== false) })} role="switch" aria-checked={formData.notifyAssignees !== false} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFormData({ ...formData, notifyAssignees: !(formData.notifyAssignees !== false) }); } }}>
                      <span className="event__switch-thumb" />
                    </span>
                  </label>
                  <button type="button" className="btn-secondary event__team-add-btn" onClick={() => { setQuickTeamErrors({}); setShowTeamQuickAdd(true); }} title="Add team member"><FaPlus /> Add</button>
                </div>
              </div>
              <div className="event__form-group">
                {/* <label>Team Members</label> */}
                <div className="event__team-list event__team-list--selectable">
                  {teamMembers.length === 0 ? (
                    <div className="event__muted">{t('event.noTeamMembers')}</div>
                  ) : (
                    teamMembers.map((member) => {
                      const checked = Array.isArray(formData.team) && formData.team.includes(member);
                      return (
                        <label key={member} className={`event__team-label ${checked ? 'is-checked' : ''}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const nextTeam = Array.isArray(formData.team) ? [...formData.team] : [];
                              if (e.target.checked) {
                                nextTeam.push(member);
                              } else {
                                const idx = nextTeam.indexOf(member);
                                if (idx > -1) nextTeam.splice(idx, 1);
                              }
                              setFormData({ ...formData, team: nextTeam });
                            }}
                          />
                        <span className="event__team-label-text">{member}</span>
                        {(() => { const fm = fullTeamMembers.find(x => String(x?.name||'').trim().toLowerCase() === String(member).trim().toLowerCase()); const role = fm?.role ? String(fm.role).trim() : ''; return role ? <span className="event__team-label-role">{role}</span> : null; })()}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {formData.orderType === 'suborder' && (
              <div className="event__form-section event__form-section--invited-by">
                <div className="event__form-section-header">
                  <h3>{t('event.sectionInvitedBy') || t('event.labelInvitedBy') || t('event.labelReferredBy')}</h3>
                </div>
                <div className="event__form-group">
                  <input
                    type="text"
                    placeholder={t('event.invitedByPlaceholder') || t('event.referredByPlaceholder')}
                    value={formData.invitedBy || formData.referredBy || ''}
                    onChange={(e) => setFormData({ ...formData, invitedBy: e.target.value, referredBy: e.target.value })}
                  />
                </div>
              </div>
            )}

            <div className="event__form-actions">
              <button className="btn-secondary" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleUpdateEvent}
                disabled={!formData.clientName || !isValidPhone(formData.clientPhone)}
              >
                <FaCheck /> Save Changes
              </button>
            </div>
          </div>
        )}
      </Modal>
      )}
      {/* Quick Add Team Member Modal */}
      <Modal isOpen={showTeamQuickAdd} onClose={() => { setShowTeamQuickAdd(false); setQuickTeamErrors({}); }} title="Add Team Member" size="small">
        <div className="team-form" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <label className="team-form__label">Name *</label>
          <input name="name" value={quickTeamForm.name} onChange={handleQuickTeamChange} className="team-form__input" placeholder="Enter name" />
          {quickTeamErrors.name && <div className="team-form__error">{quickTeamErrors.name}</div>}
          <label className="team-form__label">Phone</label>
          <input name="phone" type="tel" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} value={quickTeamForm.phone} onChange={handleQuickTeamChange} className="team-form__input" placeholder="10-digit mobile number" title="Phone: 10 digits, numbers only" />
          {quickTeamErrors.phone && <div className="team-form__error">{quickTeamErrors.phone}</div>}
          <label className="team-form__label">Email</label>
          <input name="email" value={quickTeamForm.email} onChange={handleQuickTeamChange} className="team-form__input" placeholder="Email" />
          {quickTeamErrors.email && <div className="team-form__error">{quickTeamErrors.email}</div>}
          <label className="team-form__label">Role</label>
          <input name="role" value={quickTeamForm.role} onChange={handleQuickTeamChange} className="team-form__input" placeholder="Role (e.g. Photographer)" />
          {quickTeamErrors.role && <div className="team-form__error">{quickTeamErrors.role}</div>}
          <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
            <button className="btn-primary btn-cancel" onClick={() => { setShowTeamQuickAdd(false); setQuickTeamErrors({}); }} disabled={quickTeamSaving}>Cancel</button>
            <button className="btn-primary" onClick={saveQuickTeamMember} disabled={quickTeamSaving}>{quickTeamSaving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </Modal>

      {/* Inline Add Team Payment Modal - no navigation, prefill event */}
      <Modal isOpen={showTeamPaymentModal} onClose={closeTeamPaymentModal} title={paymentMemberName || ''} size="small"
        headerActions={
          <button type="button" className="btn-secondary" onClick={() => { const name = paymentMemberName; closeTeamPaymentModal(); openPaymentsViewer(name); }} title="View all payments" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '6px 10px' }}><FaReceipt /> All Payments</button>
        }
      >
        <div className="team-form" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div className="team-form__label" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2px' }}>
            <span>Event</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={useCustomEventName} onChange={(e) => {
                setUseCustomEventName(e.target.checked);
                if (e.target.checked) { setPaymentForm({ ...paymentForm, eventId: '' }); setPaymentErrors({ ...paymentErrors, eventId: '' }); }
                else { setPaymentForm({ ...paymentForm, eventName: '' }); setPaymentErrors({ ...paymentErrors, eventName: '' }); }
              }} /> Use custom event name
            </label>
          </div>
          {!useCustomEventName ? (
            <>
              <CustomSelect value={paymentForm.eventId} onChange={handlePaymentEventChange} placeholder="Select Event" className="team-form__input"
                options={events.length === 0 ? [{ value: '', label: 'No events available' }] : events.map((ev) => ({ value: ev.id, label: `${ev.clientName || `Event ${ev.quotationId || ev.id}`} (${ev.id})` }))} />
              {paymentErrors.eventId && <div className="team-form__error">{paymentErrors.eventId}</div>}
            </>
          ) : (
            <>
              <input type="text" name="eventName" value={paymentForm.eventName} onChange={handlePaymentFormChange} className="team-form__input" placeholder="Enter event name" />
              {paymentErrors.eventName && <div className="team-form__error">{paymentErrors.eventName}</div>}
            </>
          )}
          <label className="team-form__label">Amount (₹)</label>
          <input type="number" name="amount" value={paymentForm.amount} onChange={handlePaymentFormChange} className="team-form__input" placeholder="0" step="1" min="0" />
          {paymentErrors.amount && <div className="team-form__error">{paymentErrors.amount}</div>}
          <label className="team-form__label">Payment Date</label>
          <input type="date" name="date" value={paymentForm.date} onChange={handlePaymentFormChange} className="team-form__input" />
          {paymentErrors.date && <div className="team-form__error">{paymentErrors.date}</div>}
          <label className="team-form__label">Status</label>
          <CustomSelect value={paymentForm.status} onChange={handlePaymentStatusChange} className="team-form__input" options={[{ value: 'pending', label: 'Pending' }, { value: 'partial', label: 'Partial' }, { value: 'paid', label: 'Paid' }]} />
          <label className="team-form__label">Notes (Optional)</label>
          <textarea name="notes" value={paymentForm.notes} onChange={handlePaymentFormChange} className="team-form__input" rows="3" placeholder="Add any notes about this payment..." />
          <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
            <button className="btn-primary btn-cancel" onClick={closeTeamPaymentModal} disabled={paymentSaving}>Cancel</button>
            <button className="btn-primary" onClick={saveTeamPayment} disabled={paymentSaving}>{paymentSaving ? 'Saving...' : editingPaymentId ? 'Update Payment' : 'Add Payment'}</button>
          </div>
        </div>
      </Modal>

      {/* All Payments Viewer (inline) */}
      <Modal isOpen={isPaymentsViewOpen} onClose={closePaymentsViewer} title={paymentsViewerMember?.name || ''} size="large"
        headerActions={(
          <>
            <button type="button" className="btn-secondary btn-export" onClick={exportPaymentsForViewer} title="Export payments" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', fontSize: '0.85rem' }}><FaFileExport /> Export</button>
            <button type="button" className="btn-secondary btn-add-payment" onClick={() => { closePaymentsViewer(); openTeamPaymentModal(paymentsViewerMember?.name, null); }} title="Add payment" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 10px', fontSize: '0.85rem' }}><FaPlusCircle /> Add</button>
          </>
        )}
      >
        <div className="payments-viewer">
          <div className="payments-viewer__header" style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
            <div className="payments-viewer__stat"><div className="label">Total</div><div className="value">₹{Math.round(getTotalPayments(paymentsViewerMember))}</div></div>
            <div className="payments-viewer__stat"><div className="label">Paid</div><div className="value paid">₹{Math.round(getPaidAmount(paymentsViewerMember))}</div></div>
            <div className="payments-viewer__stat"><div className="label">Pending</div><div className="value pending">₹{Math.round(getPendingAmount(paymentsViewerMember))}</div></div>
          </div>
          <div className="payments-viewer__search" style={{ marginBottom: '10px' }}><input type="search" placeholder="Search by anything..." value={paymentsSearch} onChange={(e) => { setPaymentsSearch(e.target.value || ''); setPaymentsPage(1); }} style={{ width: '100%', padding: '8px' }} /></div>
          <div className="payments-table">
            {paymentsViewerMember && (!paymentsViewerMember.payments || paymentsViewerMember.payments.length === 0) && (<p>No payments recorded for this member.</p>)}
            {paymentsViewerMember && paymentsViewerMember.payments && paymentsViewerMember.payments.length > 0 && (
              <div className="payments-table-scrollable" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th>Event ID</th><th>Event</th><th>Payment</th><th>Payment Date</th><th>Status</th><th>Notes</th><th>Actions</th></tr></thead>
                  <tbody>
                    {(() => {
                      const q = (paymentsSearch || '').trim().toLowerCase();
                      const allPayments = paymentsViewerMember.payments.slice().reverse().filter((p) => {
                        if (!q) return true;
                        const eventId = String(p.eventId||'').toLowerCase();
                        const eventName = String(p.eventName||'').toLowerCase();
                        const amount = String(p.amount||'').toLowerCase();
                        const dateStr = String(formatPayDate(p.date)).toLowerCase();
                        return eventId.includes(q) || eventName.includes(q) || amount.includes(q) || dateStr.includes(q);
                      });
                      const paginated = allPayments.slice((paymentsPage-1)*paymentsItemsPerPage, paymentsPage*paymentsItemsPerPage);
                      return paginated.map((p, idx) => {
                        const matchedEvent = events.find(ev => String(ev.id) === String(p.eventId));
                        const displayEventId = p.eventId || (matchedEvent ? matchedEvent.id : '—');
                        const displayEventName = p.eventName || (matchedEvent ? (matchedEvent.clientName || `Event ${matchedEvent.quotationId || matchedEvent.id}`) : 'Unnamed Event');
                        const rowKey = p.id || `tmp-${paymentsViewerMember._id || 'm'}-${idx}-${String(p.eventId||'').slice(0,6)}`;
                        return (
                          <tr key={rowKey}><td>{displayEventId}</td><td>{displayEventName}</td><td>₹{Math.round(p.amount||0)}</td><td>{formatPayDate(p.date)}</td><td className={`payment-status status-${p.status}`}>{p.status}</td><td title={p.notes||''}>{(p.notes||'').slice(0,30)}{(p.notes||'').length>30?'...':''}</td>
                            <td>
                              <div className="payments-viewer__actions" style={{ display: 'flex', gap: '6px' }}>
                                {p.status !== 'paid' && (
                                  <button className="payments-viewer__action-btn btn-status-change" data-tooltip="Mark Paid" aria-label="Mark Paid" onClick={async () => { const ok = await confirm('Mark this payment as paid?'); if (ok) updatePaymentStatusForEvent(paymentsViewerMember._id, p.id, 'paid'); }} style={{ padding: '4px 6px', cursor: 'pointer' }}><FaCheckCircle /></button>
                                )}
                                <button className="payments-viewer__action-btn btn-edit-payment" data-tooltip="Edit" aria-label="Edit" onClick={() => openEditPaymentForEvent(paymentsViewerMember, p)} style={{ padding: '4px 6px', cursor: 'pointer' }}><FaEdit /></button>
                                <button className="payments-viewer__action-btn btn-delete-payment" data-tooltip="Delete" aria-label="Delete" onClick={() => deletePaymentForEvent(paymentsViewerMember._id, p.id)} style={{ padding: '4px 6px', cursor: 'pointer' }}><FaTrash /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
                {(() => {
                  const q = (paymentsSearch || '').trim().toLowerCase();
                  const allPayments = paymentsViewerMember.payments.slice().reverse().filter((p) => {
                    if (!q) return true;
                    const eventId = String(p.eventId||'').toLowerCase();
                    const eventName = String(p.eventName||'').toLowerCase();
                    return eventId.includes(q) || eventName.includes(q);
                  });
                  const totalPages = Math.ceil(allPayments.length / paymentsItemsPerPage);
                  if (totalPages <= 1) return null;
                  return (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '10px', justifyContent: 'center' }}>
                      <button disabled={paymentsPage===1} onClick={() => setPaymentsPage(p=>Math.max(1,p-1))}>Prev</button>
                      <span>Page {paymentsPage} / {totalPages}</span>
                      <button disabled={paymentsPage===totalPages} onClick={() => setPaymentsPage(p=>Math.min(totalPages,p+1))}>Next</button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Manual Share Modal for non-Mivent assignees */}
      <Modal
        isOpen={showManualShareModal}
        onClose={() => { setShowManualShareModal(false); setManualShareTargets([]); setManualShareEvent(null); }}
        title="Share Event Details"
        size="medium"
      >
        <div className="share-modal">
          <p className="share-modal__lead">
            {manualShareTargets.length} team member(s) are <strong>not on Mivent</strong>, so they didn't get an automatic notification. Please send them the ready-made message below via <strong>WhatsApp</strong> or <strong>Email</strong> — it has full event details + what to do next.
          </p>
          {manualShareEvent && (
            <div className="share-modal__event">
              <div className="share-modal__event-title">{manualShareEvent.clientName || manualShareEvent.id} — {manualShareEvent.id}</div>
              <div className="share-modal__event-sub">
                {(manualShareEvent.services||[]).slice(0,3).map(s=>s.name).join(", ") || "No services"} • {manualShareEvent.date || "TBD"} {manualShareEvent.time || ""} • {manualShareEvent.services?.[0]?.location || ""}
              </div>
            </div>
          )}
          <div className="share-modal__list">
            {manualShareTargets.map((t, idx) => {
              const memberRole = t.role || resolveMemberRole(t.name);
              const waText = buildAssignmentWhatsApp({ memberName: t.name, memberRole, event: manualShareEvent || {}, assignerName, assignerPhone });
              const emailData = buildAssignmentEmail({ memberName: t.name, memberRole, event: manualShareEvent || {}, assignerName, assignerEmail, assignerPhone });
              const handleWH = () => {
                const phone = (t.phone || "").replace(/\D/g,"");
                const text = encodeURIComponent(waText);
                const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
                window.open(url, "_blank");
              };
              const handleEM = () => {
                if (!t.email) { showToast(`No email for ${t.name}`, { type:"warning" }); return; }
                const subject = encodeURIComponent(emailData.subject);
                const body = encodeURIComponent(emailData.body);
                window.location.href = `mailto:${encodeURIComponent(t.email)}?subject=${subject}&body=${body}`;
              };
              const handleCopy = async (which) => {
                const txt = which === "email" ? `Subject: ${emailData.subject}\n\n${emailData.body}` : waText;
                try { await navigator.clipboard.writeText(txt); showToast(which === "email" ? "Email message copied" : "WhatsApp message copied",{type:"success"}); } catch { showToast(txt,{duration:6000}); }
              };
              return (
                <div key={`${t.name}-${idx}`} className="share-modal__card">
                  <div className="share-modal__top">
                    <div className="share-modal__identity">
                      <span className="share-modal__avatar"><FaUser /></span>
                      <div className="share-modal__nameblock">
                        <div className="share-modal__name">
                          <span>{t.name}</span>
                          {memberRole ? <span className="share-modal__role">{memberRole}</span> : null}
                        </div>
                        <div className="share-modal__mail">{t.reason==="no_email_on_team_member" ? "No email on profile — use WhatsApp" : t.email ? t.email : "No email — use WhatsApp"}</div>
                        <div className="share-modal__contact">{t.phone ? `Phone: ${t.phone}` : "No phone — WhatsApp will ask for number"}</div>
                      </div>
                    </div>
                    <div className="share-modal__actions">
                      <button className="btn-secondary" onClick={handleWH} title="Send polished message via WhatsApp"><FaWhatsapp /> WhatsApp</button>
                      <button className="btn-secondary" onClick={handleEM} title="Send polished message via Email" disabled={!t.email}><FaEnvelope /> Email</button>
                    </div>
                  </div>
                  <details className="share-modal__preview">
                    <summary>Preview messages (WhatsApp + Email)</summary>
                    <div className="share-modal__preview-body">
                      <div>
                        <div className="share-modal__preview-row">
                          <strong>WhatsApp message</strong>
                          <button className="btn-secondary" onClick={() => handleCopy("whatsapp")} title="Copy WhatsApp message"><FaShareSquare /> Copy</button>
                        </div>
                        <pre>{waText}</pre>
                      </div>
                      <div className="share-modal__preview-divider">
                        <div className="share-modal__preview-row">
                          <strong>Email — {emailData.subject}</strong>
                          <button className="btn-secondary" onClick={() => handleCopy("email")} title="Copy Email message"><FaShareSquare /> Copy</button>
                        </div>
                        <pre>{emailData.body}</pre>
                      </div>
                    </div>
                  </details>
                </div>
              );
            })}
          </div>
          <div className="share-modal__footer">
            <button className="btn-secondary" onClick={() => { setShowManualShareModal(false); }}>Done</button>
          </div>
        </div>
      </Modal>

      {/* Shared Assigned Event Modal — B views A's event and saves as Sub Order */}
      <Modal
        isOpen={showSharedModal}
        onClose={() => { setShowSharedModal(false); setSharedEvent(null); }}
        title={sharedEvent ? `${sharedEvent.id || ""} — Assigned to you` : "Assigned Event"}
        size="large"
      >
        {sharedLoading && <div style={{ padding:"20px", textAlign:"center", color:"var(--color-text-secondary)" }}>Loading event details...</div>}
        {!sharedLoading && !sharedEvent && <div style={{ padding:"20px", textAlign:"center", color:"var(--color-danger)" }}>Event not found or not assigned to you.</div>}
        {!sharedLoading && sharedEvent && (
          <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
            <div style={{ background:"var(--color-warning-soft)", border:"1px solid var(--color-warning-border)", borderRadius:"8px", padding:"10px", fontSize:"0.9rem", color:"var(--color-warning-text)" }}>
              <FaBell style={{ marginRight:"6px" }} /> Assigned by {sharedEvent._assigner?.name || sharedEvent._assigner?.email || "team owner"}{sharedEvent._assigner?.email ? ` (${sharedEvent._assigner.email})` : ""} — save as <strong>Sub Order</strong> to track it in your own events.
            </div>
            <div style={{ background:"var(--color-surface-inset)", border:"1px solid var(--color-border)", borderRadius:"8px", padding:"10px 14px", fontSize:"0.9rem", color:"var(--color-text)" }}>
              <strong>{t('event.labelInvitedBy') || t('event.labelReferredBy') || 'Invited By'}:</strong> {getInvitedBy(sharedEvent) || sharedEvent._assigner?.name || sharedEvent._assigner?.email || '—'}
              <span style={{ color:"var(--color-text-secondary)", fontSize:"0.8rem", marginLeft:"6px" }}>(auto-filled from assigner on save)</span>
            </div>
            <div style={{ background:"var(--color-surface-inset)", border:"1px solid var(--color-border)", borderRadius:"8px", padding:"14px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"12px" }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:"1.05rem", color:"var(--color-heading)" }}>{sharedEvent.clientName || "No client"}</div>
                  <div style={{ fontSize:"0.9rem", color:"var(--color-text-secondary)", marginTop:"2px" }}>{sharedEvent.clientPhone || ""} {sharedEvent.clientPhone && sharedEvent.clientEmail ? "•" : ""} {sharedEvent.clientEmail || ""}</div>
                  {sharedEvent.quotationRef && <div style={{ fontSize:"0.85rem", color:"var(--color-text-secondary)", marginTop:"4px" }}>Quotation: {sharedEvent.quotationRef}</div>}
                </div>
                <span className={`event__status event__status--${sharedEvent.status || "scheduled"}`} style={{ whiteSpace:"nowrap" }}>{sharedEvent.status || "scheduled"}</span>
              </div>
            </div>

            <div>
              <h4 style={{ margin:"0 0 8px", display:"flex", alignItems:"center", gap:"6px", color:"var(--color-heading)" }}><FaConciergeBell style={{ color:"var(--color-primary)" }} /> Services ({(sharedEvent.services||[]).length})</h4>
              <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                {(sharedEvent.services||[]).map((s, idx)=> (
                  <div key={idx} style={{ border:"1px solid var(--color-border)", borderRadius:"8px", padding:"10px", background:"var(--color-surface)" }}>
                    <div style={{ fontWeight:600, color:"var(--color-text)" }}>{s.name || "Unnamed"}</div>
                    <div style={{ display:"flex", gap:"12px", marginTop:"4px", fontSize:"0.85rem", color:"var(--color-text-secondary)", flexWrap:"wrap" }}>
                      <span><FaCalendarAlt style={{ marginRight:"4px" }} />{s.date ? formatDateDisplay(s.date) : "TBD"}</span>
                      {s.time && <span><FaClock style={{ marginRight:"4px" }} />{s.time}</span>}
                      {s.location && <span><FaMapMarkerAlt style={{ marginRight:"4px" }} />{s.location}</span>}
                    </div>
                    {s.notes && <div style={{ marginTop:"6px", fontSize:"0.85rem", color:"var(--color-text-secondary)" }}><strong style={{ color:"var(--color-text)" }}>Notes:</strong> {s.notes}</div>}
                  </div>
                ))}
                {(!sharedEvent.services || sharedEvent.services.length===0) && <div style={{ color:"var(--color-text-muted)", fontSize:"0.9rem" }}>No services</div>}
              </div>
            </div>

            {sharedEvent.team && sharedEvent.team.length>0 && (
              <div>
                <h4 style={{ margin:"0 0 8px", display:"flex", alignItems:"center", gap:"6px", color:"var(--color-heading)" }}><FaUser style={{ color:"var(--color-primary)" }} /> Team</h4>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>{sharedEvent.team.map((m,i)=> <span key={i} style={{ background:"var(--color-surface-muted)", border:"1px solid var(--color-border)", borderRadius:"16px", padding:"4px 10px", fontSize:"0.85rem", display:"inline-flex", alignItems:"center", gap:"6px", color:"var(--color-text)" }}><FaUser style={{ color:"var(--color-text-secondary)" }} /> {m}</span>)}</div>
              </div>
            )}

            {sharedEvent.notes && <div style={{ borderLeft:"3px solid var(--color-border)", paddingLeft:"10px", fontSize:"0.9rem", color:"var(--color-body)", background:"var(--color-surface-inset)", padding:"8px 10px", borderRadius:"0 8px 8px 0" }}><strong style={{ color:"var(--color-text)" }}>Notes:</strong> {sharedEvent.notes}</div>}

            <div style={{ display:"flex", justifyContent:"flex-end", gap:"10px", marginTop:"4px" }}>
              <button className="btn-secondary" onClick={()=> { setShowSharedModal(false); setSharedEvent(null); }}>Close</button>
              <button className="btn-primary" onClick={handleSaveAsSubOrder} disabled={saveSubOrderLoading} style={{ display:"inline-flex", alignItems:"center", gap:"8px" }}>
                {saveSubOrderLoading ? "Saving..." : <><FaPlusCircle /> Save as Sub Order</>}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Client share dialog: To Client vs To Others (WhatsApp / Email) */}
      <ShareChoiceModal
        isOpen={!!clientShareTarget}
        onClose={() => setClientShareTarget(null)}
        channel={clientShareChannel}
        title={clientShareChannel === 'whatsapp' ? 'Share Event via WhatsApp' : 'Share Event via Email'}
        docLabel={clientShareTarget ? `${getEventIdentifier(clientShareTarget)}${clientShareTarget.clientName ? ` • ${clientShareTarget.clientName}` : ''}` : ''}
        clientName={clientShareTarget?.clientName || ''}
        clientContact={clientShareChannel === 'whatsapp' ? (clientShareTarget?.clientPhone || '') : (clientShareTarget?.clientEmail || '')}
        hasClientContact={clientShareChannel === 'whatsapp' ? !!normalizeClientSharePhone(clientShareTarget?.clientPhone) : !!((clientShareTarget?.clientEmail || '').trim())}
        clientMessage={clientShareTarget ? (clientShareChannel === 'whatsapp' ? buildEventWhatsApp({ event: clientShareTarget, company, audience: 'client' }) : buildEventEmail({ event: clientShareTarget, company, audience: 'client' }).body) : ''}
        othersMessage={clientShareTarget ? (clientShareChannel === 'whatsapp' ? buildEventWhatsApp({ event: clientShareTarget, company, audience: 'others' }) : buildEventEmail({ event: clientShareTarget, company, audience: 'others' }).body) : ''}
        clientSubject={clientShareTarget && clientShareChannel === 'email' ? buildEventEmail({ event: clientShareTarget, company, audience: 'client' }).subject : ''}
        othersSubject={clientShareTarget && clientShareChannel === 'email' ? buildEventEmail({ event: clientShareTarget, company, audience: 'others' }).subject : ''}
        onSendToClient={handleClientShareToClient}
        onShareOthersViaApp={handleClientShareOthersViaApp}
      />

      {/* Floating event calendar — same calendar as dashboard, above contact FAB */}
      <EventCalendarFab
        events={events}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSelectDay={(iso) => {
          setDateFrom(iso);
          setDateTo(iso);
          setCurrentPage(1);
        }}
        onClearDay={() => {
          setDateFrom('');
          setDateTo('');
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default Event;
